import { serverInfoController } from "@/app/serverInfoController";
import {
  maskGenerationBlockReason,
  isMaskGenerationDisabledError,
  MASK_GENERATION_DISABLED_MESSAGE,
} from "@/app/trainingMaskGeneration";
import { toast } from "@carbon6/c6-frontend-design";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import {
  adjacentConfigureSection,
  buildConfigureSections,
  resolveSelectableConfigureSection,
  selectableConfigureSections,
  type ConfigureSection,
  type EvaluationSectionInput,
  type LockedEvaluationSection,
} from "./configureSections";
import { annotationMaskAdvice } from "./annotationMaskAdvice";
import { reconcileSigmaDefaults, resetSigmaConfigDefaults } from "./sigmaDefaultEdits";
import {
  emptySigmaDefaultsState,
  isSigmaDatasetSelection,
  sigmaDatasetDefaults,
  type SigmaDefaultsState,
} from "./sigmaDatasetDefaults";
import { isAnnotationSetSelectable, type AnnotationSetsSource } from "./annotationSetSelectable";
import {
  hasSimulationDataset,
  ISAAC_EVALUATION_SIMULATION_ONLY_HINT,
} from "./isaacEvaluationAvailability";
import type { SubmitFormPrefill } from "./sessionResubmitPrefill";
import {
  EMPTY_SCHEDULE_DRAFT,
  isScheduleDraftReady,
  scheduleCreateFields,
  type ScheduleDraft,
} from "./scheduleDraft";
import { useGpuCapacityTotal } from "@/components/gpu";
import type { UserRole } from "@/app/userRole";
import {
  createExperiment,
  createTrainingSchedule,
  channelSelectionFromSuggestion,
  getDataset,
  getDatasetDetails,
  getDatasetStructure,
  getTrainingBranchConfig,
  getTrainingSubmitOptions,
  getWorkspaceNotifications,
  isNoTrainingTeamError,
  isSelfNotificationOn,
  isTrainingAnnotationSetUnusableError,
  isTrainingPolicyNotFoundError,
  isWorkspaceNotOwnedError,
  listAnnotationSets,
  listMyExperiments,
  listSharedExperiments,
  listMyWorkspaces,
  listSharedWorkspaces,
  listTrainingBranchCommits,
  listTrainingBranchConfigs,
  loadScopedPolicies,
  previewTrainingOverrides,
  submitTrainingSession,
  toggleSelectedChannel,
  updateExperiment,
  validateTrainingSession,
  type AnnotationSetSummaryView,
  type BranchCommitsView,
  type BranchConfigEntryView,
  type BranchConfigsView,
  type ChannelSelectionTopic,
  type ChannelSelection,
  type Dataset,
  type DatasetDetails,
  type DatasetStructure,
  type IsaacEvaluationReq,
  type OverridePreview,
  type SubmitOptionsView,
  type TrainingExperiment,
  type TrainingPolicyView,
  type TrainingScheduleView,
  type TrainingSessionView,
  type TrainingValidationReport,
  type TrainingWorkspace,
} from "@/api";
import { type LoadState, resolveLoadErrorMessage } from "@/components/dataset";
import { isFullSha } from "@/components/table/branchHash";
import { isHttpClientError } from "@/http";
import { taskValue } from "@/page/training/evalParams/evalParamSpecs";
import { resolveSeedPlan, type SeedPlan } from "@/page/training/evalParams/evalSeeds";
import { reconcileSeedScenarios } from "@/page/training/evalParams/reconcileScenario";
import {
  useEvalImageParams,
  type EvalImageParamsState,
} from "@/page/training/evalParams/useEvalImageParams";
import {
  BATCH_SIZE_OVERRIDE_KEY,
  computeAutoLr,
  imageSizeAt,
  isBudgetForm,
  LR_OVERRIDE_KEY,
  parseOverrideKey,
  parseRecipe,
  seedOverrideRows,
  type ParsedRecipe,
} from "./recipe";
import {
  bakeOverridesIntoYaml,
  buildPreviewPayload,
  buildSubmitPayload,
  buildValidatePayload,
  canFollowBranchTip,
  canSubmitForm,
  effectiveConfigOverridePairs,
  emptyConfigState,
  emptySubmitForm,
  hasConfigEdit,
  isDirectConfigEdit,
  isStepComplete,
  learningCoreRef,
  reservedInitOverrideKeys,
  saveOnlyLastCheckpointSaveEvery,
  type KeyValuePair,
  type SubmitConfigState,
  type SubmitDatasetSelection,
  type SubmitFormState,
  type SubmitInitPolicy,
  type SubmitInitSourceSession,
  type SubmitInitState,
  type SubmitStep,
  withLockedRole,
  withSyncedAnnotationSets,
} from "./submitWizardForm";
import {
  hasScheduleStep,
  initStrategyLockReason,
  submitWizardFeatures,
  type SubmitWizardFeatures,
  type SubmitWizardMode,
} from "./submitWizardFeatures";

// 워크스페이스는 admin 이 책임지는 개념이라 문구에도 넣지 않는다(docs/rules.md §15).
// 두 경우 모두 사용자가 할 수 있는 일은 관리자에게 요청하는 것 하나다.
const NO_TEAM_MESSAGE = "Training 팀 매핑이 필요합니다. 관리자에게 요청해 주세요.";
const NOT_OWNED_MESSAGE = "이 Training 환경을 쓸 권한이 없습니다. 관리자에게 요청해 주세요.";
// schedule 모드. 공용 workspace 제출은 research 그룹 소속이 조건이다(BE resolve_submit_group 의
// 공용 경로). admin 이어도 소속이 없으면 같은 403 이 온다.
const SHARED_NOT_OWNED_MESSAGE =
  "이 공용 Workspace에 제출하려면 운영자가 research 그룹에도 속해야 합니다. 관리자에게 요청해 주세요.";
const NO_SHARED_WORKSPACE_MESSAGE =
  "등록된 그룹 공용 Workspace가 없습니다. Schedule 페이지의 [그룹 공용 Workspace 관리]에서 먼저 등록해 주세요.";
// 부재와 스코프 밖이 같은 404 라서 "권한 없음"으로 구분하지 않는다(존재 노출 방지 계약).
const POLICY_NOT_FOUND_MESSAGE =
  "선택한 시작점 Policy를 찾을 수 없습니다. 목록을 다시 불러와 골라 주세요.";

// 고른 어노테이션 세트를 지금은 쓸 수 없다. 부재·삭제·draft·렌더 미완을 한 문구로 표시한다.
// 카드가 후보를 다시 받으면 그 줄에 원인을 표시하므로 여기서 사유를 나눠 적지 않는다.
const ANNOTATION_SET_UNUSABLE_MESSAGE =
  "고른 Annotation Set을 지금은 쓸 수 없습니다. Dataset 카드에서 세트를 다시 골라 주세요.";

// fine-tune 제안의 구간 조회(`since`) 창. BE 최대값이다. 기본 목록은 BE 기본(50)을 따른다. 제안 sha 가
// 창 밖이면 응답이 `since_reached=false` 를 주고, 화면이 합성 옵션을 만든다.
export const BRANCH_COMMITS_SINCE_LIMIT = 200;

// finetune 기본 레시피. BE 는 config 키를 보내지 않은 제출에만 이 이름으로 치환하지만, FE 는
// 항상 config 를 보낸다. 여기서 고르지 않으면 finetune 이 알림 없이 scratch 용 default 로 실행된다.
const FINETUNE_CONFIG_NAME = "default_fine_tune";

// 편집 중인 실험 노트 하나. null 이면 서버 값을 그대로 표시하는 상태다(별도 editing 플래그를
// 두지 않는다). 첫 타이핑이 초안을 만들고, 저장·되돌리기가 초안을 닫는다.
export type ExperimentNoteDraft = {
  text: string;
  saving: boolean;
  error: string | null;
};

// 노트 본문의 wire 정규화. 앞뒤 공백을 제거하고 빈 본문은 null(노트 없음)로 둔다. 저장 요청과
// "바뀌었는가" 판정이 같은 정규화를 읽어야 공백만 입력한 초안이 저장 버튼을 켜지 않는다.
export function normalizeExperimentNote(text: string | null | undefined): string | null {
  const trimmed = text?.trim() ?? "";
  return trimmed === "" ? null : trimmed;
}

// 초안이 서버 노트와 다른지. 저장 버튼의 활성 조건이고, 제출 직전 자동 저장의 "보낼 값이 있는가" 판정이다.
export function experimentNoteChanged(
  draftText: string,
  savedNote: string | null | undefined,
): boolean {
  return normalizeExperimentNote(draftText) !== normalizeExperimentNote(savedNote);
}

// 제출 직전 노트 저장이 실패했을 때 submitError 의 앞부분. 세션이 아니라 노트가 실패했다는 사실을
// 한 자리의 오류 문구가 표시해야 한다.
export const EXPERIMENT_NOTE_SAVE_FAILED_PREFIX = "실험 노트를 저장하지 못해 제출을 멈췄습니다. ";

// 제출 한 번의 결과.
export type SubmitOutcome = { ok: boolean };

// 선택 데이터셋 uuid 목록(`"a|b"`)의 항목마다 하나씩 받아 오는 로더. 아직 모르는 uuid 만 서로
// 독립 요청으로 함께 받고, 캐시가 있어서 목록을 오가도 다시 조회하지 않는다. 순서가 바뀐 응답이
// 상태를 덮어쓰지 않도록 dispose 를 가드한다. 구조와 상세가 각각 이 훅을 한 번씩 쓰므로
// 한쪽 실패가 다른 쪽을 실패시키지 않는다.
function usePerDatasetLoad<T>(
  datasetKey: string,
  fetchOne: (uuid: string) => Promise<T>,
  uuidOf: (item: T) => string,
): LoadState<T[]> {
  const [state, setState] = useState<LoadState<T[]>>({ status: "idle" });
  const cacheRef = useRef(new Map<string, T>());
  useEffect(() => {
    const uuids = datasetKey === "" ? [] : datasetKey.split("|");
    if (uuids.length === 0) {
      setState({ status: "idle" });
      return;
    }
    const cache = cacheRef.current;
    if (uuids.every((uuid) => cache.has(uuid))) {
      setState({ status: "success", data: uuids.map((uuid) => cache.get(uuid)!) });
      return;
    }
    let disposed = false;
    setState({ status: "loading" });
    Promise.all(uuids.map((uuid) => cache.get(uuid) ?? fetchOne(uuid)))
      .then((items) => {
        items.forEach((item) => cache.set(uuidOf(item), item));
        if (!disposed) {
          setState({ status: "success", data: items });
        }
      })
      .catch((error) => {
        if (!disposed) {
          setState({ status: "error", message: resolveLoadErrorMessage(error) });
        }
      });
    return () => {
      disposed = true;
    };
    // fetchOne·uuidOf 는 모듈 함수라 안정적이다. 실제로 목록이 바뀔 때만 다시 실행된다.
  }, [datasetKey, fetchOne, uuidOf]);
  return state;
}

const structureUuid = (structure: DatasetStructure): string => structure.dataset_uuid;
const datasetUuid = (dataset: Dataset): string => dataset.uuid;
const cameraDetailsUuid = (details: DatasetDetails): string => details.dataset_uuid;

// 입력 하나의 어노테이션 세트 후보. `listAnnotationSets` 는 배열을 반환하므로 캐시 키를 항목에서
// 얻을 수 없다. **세트가 0건이면 항목이 없다.** 그래서 요청한 uuid 를 함께 담아 usePerDatasetLoad
// 의 uuidOf 를 만족시킨다.
type DatasetAnnotationSets = { dataset_uuid: string; sets: AnnotationSetSummaryView[] };
const fetchAnnotationSets = (uuid: string): Promise<DatasetAnnotationSets> =>
  listAnnotationSets(uuid).then((sets) => ({ dataset_uuid: uuid, sets }));
const annotationSetsUuid = (entry: DatasetAnnotationSets): string => entry.dataset_uuid;

type IsaacEvaluationPlan = {
  ready: boolean;
  task: string;
  seedPlan: SeedPlan | null;
  errorMessage: string | null;
};

function resolveIsaacEvaluationPlan(
  enabled: boolean,
  params: EvalImageParamsState,
): IsaacEvaluationPlan {
  if (!enabled) {
    return { ready: true, task: "", seedPlan: null, errorMessage: null };
  }
  if (params.status !== "success" || params.specsStatus !== "success") {
    return { ready: false, task: "", seedPlan: null, errorMessage: null };
  }
  if (params.imageUri === null || params.selectedTag === "") {
    return {
      ready: false,
      task: "",
      seedPlan: null,
      errorMessage: "Select a Docker image for evaluation.",
    };
  }
  const task = taskValue(params.specs, params.values);
  if (task === "") {
    return {
      ready: false,
      task,
      seedPlan: null,
      errorMessage: "Select a TASK before running evaluation.",
    };
  }
  try {
    return {
      ready: true,
      task,
      seedPlan: resolveSeedPlan(params.values),
      errorMessage: null,
    };
  } catch (error: unknown) {
    return {
      ready: false,
      task,
      seedPlan: null,
      errorMessage: error instanceof Error ? error.message : "Check the SEED values.",
    };
  }
}

// submit·experiment 생성 공통 에러 문구. 팀 미매핑(403)과 비선점 workspace(403)를 구분해 안내한다.
// 공용 workspace 는 선점이 아니라 등록이라 같은 403 의 뜻이 다르다.
function resolveSubmitErrorMessage(
  error: unknown,
  ownership: SubmitWizardFeatures["ownership"],
): string {
  if (isMaskGenerationDisabledError(error)) return MASK_GENERATION_DISABLED_MESSAGE;
  if (isNoTrainingTeamError(error)) {
    return NO_TEAM_MESSAGE;
  }
  if (isWorkspaceNotOwnedError(error)) {
    return ownership === "shared" ? SHARED_NOT_OWNED_MESSAGE : NOT_OWNED_MESSAGE;
  }
  if (isTrainingPolicyNotFoundError(error)) {
    return POLICY_NOT_FOUND_MESSAGE;
  }
  if (isTrainingAnnotationSetUnusableError(error)) {
    return ANNOTATION_SET_UNUSABLE_MESSAGE;
  }
  return resolveLoadErrorMessage(error);
}

// 같은 id 가 있으면 교체하고, 없으면 이름순 위치에 추가한다. 목록 API 가 주는 이름순과 같은 순서다.
function upsertExperiment(
  items: readonly TrainingExperiment[],
  next: TrainingExperiment,
): TrainingExperiment[] {
  const rest = items.filter((item) => item.id !== next.id);
  return [...rest, next].sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
}

// 이름으로 config 경로를 찾는다. 목록은 `configs/foo.yaml` 같은 저장소 경로라 말단만 비교한다.
function findConfigPath(
  configs: readonly BranchConfigEntryView[],
  name: string,
): string | undefined {
  const fileName = `${name}.yaml`;
  return configs.find((entry) => entry.path === fileName || entry.path.endsWith(`/${fileName}`))
    ?.path;
}

// 기본 config 선택 정책. scratch 는 default.yaml 을 먼저 쓰고, 없으면 첫 항목을 쓴다. finetune 은
// default_fine_tune 만 기본으로 인정한다. 찾지 못했을 때 임의의 config 를 고르면 finetune 이
// 알림 없이 scratch 용 레시피로 실행된다. 브랜치에 없다는 사실을 알리는 것이 더 낫다.
function resolveDefaultConfigPath(
  configs: readonly BranchConfigEntryView[],
  finetune: boolean,
): { path: string | undefined; finetuneMissing: boolean } {
  if (!finetune) {
    return { path: findConfigPath(configs, "default") ?? configs[0]?.path, finetuneMissing: false };
  }
  const path = findConfigPath(configs, FINETUNE_CONFIG_NAME);
  return { path, finetuneMissing: path === undefined };
}

function finetuneConfigMissingMessage(): string {
  return `이 Branch 에 ${FINETUNE_CONFIG_NAME} 레시피가 없습니다. finetune 에 맞는 config 를 직접 골라 주세요.`;
}

// 잠긴 Isaac Evaluation 섹션 값. 모듈 상수다. 잠긴 동안 다른 평가 파라미터가 바뀌어도
// identity 가 안정적이어서 configureSections 를 다시 만들지 않는다.
const SIMULATION_LOCKED_EVALUATION: LockedEvaluationSection = {
  lockedReason: ISAAC_EVALUATION_SIMULATION_ONLY_HINT,
};

export type UseSubmitWizardControllerOptions = {
  role?: UserRole | null;
  // 이 위저드가 만드는 대상. 단건 세션 제출(기본) 또는 반복 스케줄이다. 포크 대신 mode 를 쓰는 이유:
  // 앞 두 스텝(데이터셋·구성)과 payload 조립이 완전히 같아서, 나누면 같은 폼이 두 벌이 된다.
  // 두 모드의 차이는 `submitWizardFeatures` 표가 소유하고, 여기서는 어느 행인지만 고른다.
  mode?: SubmitWizardMode;
  // 스케줄 생성이 성공하면(생성된 스케줄 view) 호출한다. 목록 방송과 창 닫기는 호출부가 한다.
  // mode:"schedule" 에서만 쓴다.
  onScheduled?: (view: TrainingScheduleView) => void;
  // Isaac Evaluation 사용 권한(서버 플래그, canUseEvaluation). 없으면 섹션이 권한 사유로 잠긴다.
  // 다른 게이트처럼 fail closed 를 지키도록 기본값 없이 호출부가 항상 값을 전달한다.
  canUseIsaacEvaluation: boolean;
  // 제출이 성공하면(생성된 세션 view) 호출한다. 상세 Window 열기와 제출 Window 닫기는 호출부가 한다.
  onSubmitted: (view: TrainingSessionView) => void;
  // 데이터셋이 이미 정해진 진입점(데이터셋 상세 [학습 시작] 등)의 초기 선택. 폼에 시드되고
  // 카메라 키도 마운트할 때 적재된다. 사용자는 picker 에서 다른 데이터셋으로 바꿀 수 있다.
  initialDataset?: SubmitDatasetSelection;
  // 실패 세션 "설정 고쳐 다시 제출" 진입점의 폼 전체 시드(ZERG-257). 마운트할 때 1회만 쓴다.
  // 그 뒤 폼은 사용자가 소유한다.
  prefill?: SubmitFormPrefill;
  // 내 알림 수신 이메일(/v1/me notification_email). 알림 토글 값을 상류 수신자 행에서 읽는
  // 키다. 로그인 이메일이 아니다. 없으면(미설정) 수신자가 될 수 없으므로 알림 값을 시드하지
  // 않는다(제출은 그대로 된다).
  selfNotificationEmail?: string | null;
};

// 본문 직접 편집으로 바꿀 때 비운 사용자 override 행과 auto-LR 토글. 돌아올 때 그대로 복원한다.
type OverridesBeforeDirectEdit = {
  overrides: KeyValuePair[];
  autoLr: boolean;
  sigmaWrists?: SigmaDefaultsState["wrists"];
  sigmaRemovedWrists?: SigmaDefaultsState["removedWrists"];
  sigmaImageSize?: SigmaDefaultsState["imageSize"];
  imageSize: SubmitConfigState["imageSize"];
};

// 직접 편집에서 overrides 방식으로 돌아올 때의 폼. 비워 둔 행과 auto-LR 토글을 스냅샷에서 복원한다.
// StrictMode가 updater를 다시 호출해도 같은 스냅샷을 써야 한다. ref는 호출부에서 비운다.
function restoreOverridesBeforeDirectEdit(
  prev: SubmitFormState,
  snapshot: OverridesBeforeDirectEdit | null,
): SubmitFormState {
  if (snapshot === null) {
    return prev;
  }
  const discardYamlImageSize = prev.sigmaDefaults?.imageSize === "yaml";
  return {
    ...prev,
    autoLr: snapshot.autoLr,
    sigmaDefaults:
      prev.sigmaDefaults === undefined
        ? undefined
        : {
            ...prev.sigmaDefaults,
            wrists: snapshot.sigmaWrists ?? {},
            removedWrists: snapshot.sigmaRemovedWrists ?? {},
            imageSize: discardYamlImageSize
              ? (snapshot.sigmaImageSize ?? null)
              : prev.sigmaDefaults.imageSize,
          },
    config: {
      ...prev.config,
      overrides: snapshot.overrides,
      imageSize: discardYamlImageSize ? snapshot.imageSize : prev.config.imageSize,
    },
  };
}

// New Training 위저드의 데이터 적재·폼 상태·제출을 한곳에서 소유한다. 창을 열면 submit-options 를
// 받고 기본 브랜치의 config 목록까지 미리 적재한다(사용자가 실험 설정 스텝에 있는 동안 준비한다).
// experiments·config 본문은 선택 시점에 로드하고, POST /sessions(Idempotency-Key)로 제출한다. mode:"schedule" 은
// 같은 payload 를 POST /schedules 로 보낸다(멱등 키 없음. 실패 재시도는 확정 스텝 문구가 안내한다).
export function useSubmitWizardController({
  role = null,
  mode = "session",
  onScheduled,
  canUseIsaacEvaluation,
  onSubmitted,
  initialDataset,
  prefill,
  selfNotificationEmail = null,
}: UseSubmitWizardControllerOptions) {
  // 이 위저드가 표시하는 항목과 고를 수 있는 항목. mode 가 위저드 수명 동안 불변이라
  // 표도 불변이고, 참조가 안정적이어서 memo 의존성에 그대로 넣는다.
  const features = submitWizardFeatures(mode);
  const steps = features.steps;
  const [optionsState, setOptionsState] = useState<LoadState<SubmitOptionsView>>({
    status: "idle",
  });
  const [form, setForm] = useState<SubmitFormState>(() => {
    const base = {
      ...emptySubmitForm({ nodes: 1, gpusPerNode: 1 }),
      // 초기 데이터셋은 폼 초기값으로 시드한다. picker 가 이 선택을 덮어쓰지 않는다.
      datasets: initialDataset === undefined ? [] : [initialDataset],
    };
    if (prefill === undefined) {
      return base;
    }
    // nodes·gpusPerNode 는 여기서 시드하지 않는다. options 로드가 리소스 기본값을 덮어쓰므로
    // 그 시점에 prefill 우선으로 함께 정한다. null 은 "원본 미지정"이라 폼 기본값을 유지한다.
    return {
      ...base,
      // 프리필도 담기 경로와 같은 규칙을 따른다. 같은 Dataset 이 두 번 들어오면 세트를 한 값으로
      // 합친다(setDatasets 를 거치지 않는 유일한 입구다).
      datasets: withSyncedAnnotationSets(prefill.datasets),
      targetDatasetIndex: prefill.targetDatasetIndex,
      // 재학습 진입점은 시작점을 소스 세션으로 고정해서 연다. 여기서 정하지 않으면 사용자가
      // 위저드 안에서 복원할 방법이 없다. 소스는 진입점만 정할 수 있다.
      init:
        prefill.retrainSource === null
          ? base.init
          : { strategy: "retrain", source: prefill.retrainSource },
      // 수명 하한만 값이 없을 때 폼 기본으로 합친다. 상류도 값이 없으면 4시간을 주입하므로 합친 결과가
      // 원본 동작과 같다. 스팟은 합치지 않는다. 상류 기본이 허용이라, 값이 없는 것을 폼 기본(`false`)으로
      // 합치면 재제출이 원본에 없던 "스팟 금지"를 명시해서 보낸다.
      minLifetimeSeconds: prefill.minLifetimeSeconds ?? base.minLifetimeSeconds,
      allowSpot: prefill.allowSpot,
      displayName: prefill.displayName,
    };
  });
  const [step, setStep] = useState<SubmitStep>("dataset");
  // 스케줄 초안(mode:"schedule" 전용). 제출 payload 에 포함되지 않는 값이라 폼이 아니라
  // 여기서 소유한다(scheduleDraft 모듈 주석 참조). session mode 에서는 쓰이지 않는다.
  const [scheduleDraft, setScheduleDraft] = useState<ScheduleDraft>(EMPTY_SCHEDULE_DRAFT);
  // 여유 GPU 조건의 실질 상한. 사이드바 위젯 스냅샷을 함께 읽는다(왕복 없음).
  const totalGpus = useGpuCapacityTotal();
  const scheduleDraftRef = useRef(scheduleDraft);
  scheduleDraftRef.current = scheduleDraft;
  const updateScheduleDraft = useCallback((patch: Partial<ScheduleDraft>) => {
    setScheduleDraft((prev) => ({ ...prev, ...patch }));
  }, []);
  // 구성 스텝에서 펼친 섹션. 창 셸이 ↑/↓ 를 이 값으로 전달해야 하므로 스텝과 같은 곳에 둔다.
  // 마법사 내비 상태가 흩어지면 창은 스텝은 알지만 섹션은 모르는 상태가 된다.
  // 이 값은 사용자가 고른 그대로다. 밖으로는 고를 수 있는 목록으로 되돌린 값(`configureSection`)만 전달한다.
  // 그래서 시뮬레이션 데이터셋을 빼서 Isaac Evaluation 이 잠겨도 화면이 잠긴 섹션을 가리키지 않는다.
  const [configureSectionRaw, setConfigureSection] = useState<ConfigureSection>("experiment");
  // 워크스페이스는 내(me) 스코프만 쓴다. 팀 전체가 아니라 actor 에게 매핑된 것만 쓴다(Training 의 me Policy 와 같은 소스).
  const [workspacesState, setWorkspacesState] = useState<LoadState<TrainingWorkspace[]>>({
    status: "idle",
  });
  const [experimentsState, setExperimentsState] = useState<LoadState<TrainingExperiment[]>>({
    status: "idle",
  });
  // 새 실험 생성 다이얼로그 상태(null = 닫힘).
  const [experimentDialog, setExperimentDialog] = useState<{
    submitting: boolean;
    error: string | null;
  } | null>(null);
  // 실험 노트 초안(null = 서버 값 그대로). 실험은 여러 세션이 공유하는 단위라 타이핑을 바로
  // 보내지 않고 명시 저장(또는 제출 직전 자동 저장)으로만 쓴다.
  const [experimentNote, setExperimentNote] = useState<ExperimentNoteDraft | null>(null);
  const [branchConfigsState, setBranchConfigsState] = useState<LoadState<BranchConfigsView>>({
    status: "idle",
  });
  const [configBodyLoading, setConfigBodyLoading] = useState(false);
  // 고른 브랜치의 최근 커밋. Commit 드롭다운의 소스다. 실패해도 제출을 막지 않는다(목록만
  // 비고 "브랜치 최신"은 계속 고를 수 있다).
  const [commitsState, setCommitsState] = useState<LoadState<BranchCommitsView>>({
    status: "idle",
  });
  // config 를 바꾸면서 편집본을 버렸는지. 화면이 한 번 알리고, 다음 편집에서 안내가 사라진다.
  const [configEditDiscarded, setConfigEditDiscarded] = useState(false);
  // 본문 직접 편집으로 바꿀 때 비운 사용자 override 행과 auto-LR 토글. 돌아올 때 그대로 복원한다.
  // 위저드 수명의 화면 밖 상태라 폼이 아니라 여기 둔다. null = 직접 편집 중이 아니다.
  const overridesBeforeDirectEditRef = useRef<OverridesBeforeDirectEdit | null>(null);
  // finetune 시작점 후보(Policy 카탈로그). 스코프는 내/팀 토글이고 필터는 적용하지 않는다.
  // 시작점이 지금 고른 workspace 에 속할 필요가 없다.
  const [initPolicyScope, setInitPolicyScope] = useState<"me" | "team">("me");
  const [initPolicyState, setInitPolicyState] = useState<LoadState<readonly TrainingPolicyView[]>>({
    status: "idle",
  });
  // finetune 기본 config 가 브랜치에 없을 때의 안내(있으면 null). 제출은 계속 할 수 있다.
  const [finetuneConfigNotice, setFinetuneConfigNotice] = useState<string | null>(null);
  // 재학습 시작점인 소스 세션. 진입점(세션 상세)이 정하고 위저드 수명 동안 바뀌지 않는다.
  // 세그먼트를 scratch 로 바꿨다가 돌아와도 소스를 잃지 않도록 폼 밖에 둔다.
  const [retrainSource] = useState<SubmitInitSourceSession | null>(
    () => prefill?.retrainSource ?? null,
  );
  // 선택된 config 본문의 파싱 결과. 계열(family) 감지로 override 키 후보를 좁히는 데 쓴다.
  const [parsedConfig, setParsedConfig] = useState<ParsedRecipe | null>(null);
  // 서버에서 받은 원본 본문. dirty 판정과 "원본으로 되돌리기"의 기준이다.
  const [sourceYaml, setSourceYaml] = useState("");
  // 프리필로 승계한 세트를 쓸 수 없어서 비운 입력 수. setDatasets(사용자 조작)와 후보 판정 effect 가
  // 함께 쓰므로 그 둘보다 앞에 둔다.
  const [droppedAnnotationSetCount, setDroppedAnnotationSetCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const serverInfo = useSyncExternalStore(
    serverInfoController.subscribe,
    serverInfoController.getSnapshot,
  );
  const maskGenerationBlocked = maskGenerationBlockReason(serverInfo);
  useEffect(() => {
    void serverInfoController.refresh();
  }, []);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // 상류가 eval 설정에 대해 준 비치명 안내(이미지가 읽지 않는 파라미터 등). 제출을 막지
  // 않으므로 오류와 같은 자리에 두지 않는다. 확정 스텝이 hint 로 표시한다.
  const [evalWarnings, setEvalWarnings] = useState<readonly string[]>([]);
  // 사용자가 켠 값 그대로다. 밖으로는 시뮬레이션 데이터셋이 있을 때만 켜진 것으로 보인다(아래
  // `isaacEvaluationEnabled`). 값을 되돌리지 않고 마스킹만 하므로, 데이터셋을 바꿨다가 돌아오면
  // 켠 상태가 유지된다.
  const [isaacEvaluationEnabledRaw, setIsaacEvaluationEnabled] = useState(false);
  // 같은 위저드 인스턴스의 제출은 같은 키를 재사용한다. 재시도해도 중복 생성되지 않는다(BE 멱등).
  const idempotencyKeyRef = useRef<string>(crypto.randomUUID());
  // async 콜백이 최신 폼을 읽기 위한 ref(상태 갱신 경쟁 회피).
  const formRef = useRef(form);
  formRef.current = form;
  // 저장 콜백이 최신 초안을 읽기 위한 ref. 저장 버튼 콜백이 타이핑마다 새로 만들어지지 않게 한다.
  const experimentNoteRef = useRef(experimentNote);
  experimentNoteRef.current = experimentNote;
  // 저장·제출 콜백이 "서버에 있는 노트"를 읽기 위한 ref. 목록이 갱신될 때마다 콜백을 다시
  // 만들지 않는다.
  const experimentsStateRef = useRef(experimentsState);
  experimentsStateRef.current = experimentsState;
  // learning-core ref(브랜치 이름 또는 고정 커밋)별 config 목록 캐시. 오가도 다시 요청하지 않는다
  // (위저드 인스턴스 수명).
  const branchConfigsCacheRef = useRef(new Map<string, BranchConfigsView>());
  // 마지막으로 요청한 learning-core ref(= 제출될 ref). 선택 시점에 동기로 기록해서, 순서가 바뀐
  // 응답이 상태를 덮어쓰지 않게 한다. 브랜치 이름이 아니라 effective ref 를 쓰는 이유: 커밋을 고정하면
  // config 목록·본문도 그 커밋의 값을 받아야 화면이 제출물과 같다.
  const requestedLearningCoreRef = useRef("");
  // 지금 화면의 config 목록·본문이 속한 트리(해석된 sha). 같은 트리로의 전환(브랜치 tip 을 그대로
  // 고정·고정 해제)은 본문이 같으므로 다시 받지 않고 편집본도 바꾸지 않는다.
  const loadedTreeShaRef = useRef<string | null>(null);
  // 브랜치별 커밋 목록 캐시(키 = `브랜치|since`). 늦은 응답은 effect cleanup 이 버린다.
  const branchCommitsCacheRef = useRef(new Map<string, BranchCommitsView>());
  // config 본문 요청 세대. 같은 브랜치 안에서 A→B 를 빠르게 고르거나 브랜치를 바꿨을 때
  // 먼저 시작한 요청의 parsed/source/loading 상태가 최신 선택을 덮어쓰지 못하게 한다.
  const requestedConfigLoadRef = useRef(0);
  // 프리필의 브랜치·리소스 값. options 로드 시점에 1회 사용한다(마운트할 때의 값으로 고정).
  const prefillRef = useRef(prefill);
  // 프리필의 config 이름. 첫 config 자동 선택에서 1회 사용한다. recipe 의 이름은 정규화
  // 형태라 findConfigPath 로 브랜치 목록의 경로를 찾는다.
  const prefillConfigNameRef = useRef(prefill?.configName ?? null);
  // 프리필 override 행. 위 이름으로 고른 config 의 본문이 도착하면 팀 관례 시드 대신 쓴다.
  const prefillOverrideRowsRef = useRef<{ configPath: string; rows: KeyValuePair[] } | null>(null);

  // config 본문 로드. <ref, config> 의 본문을 받아 파싱하고 폼에 넣는다. 요청 세대로 늦은 응답을
  // 버린다. 본문은 계열 감지로 override 키 후보를 좁히고, 오른쪽 read-only 본문과 확정 diff 의 기준이
  // 되며, override 행을 팀 관례 시드로 대체한다. 실패해도 이름 제출은 계속 할 수 있다(graceful).
  // `keepOverrides` 는 같은 config 를 다른 커밋에서 다시 받는 리비전 전환 경로가 켠다. 이때 사용자
  // override 행을 시드로 덮어쓰지 않고 프리필 행도 사용하지 않는다.
  const loadConfigBody = useCallback((ref: string, configName: string, keepOverrides: boolean) => {
    requestedConfigLoadRef.current += 1;
    const requestId = requestedConfigLoadRef.current;
    setParsedConfig(null);
    setSourceYaml("");
    setConfigBodyLoading(false);
    if (configName === "" || ref === "") {
      return;
    }
    setConfigBodyLoading(true);
    getTrainingBranchConfig(ref, configName)
      .then((view) => {
        if (requestedConfigLoadRef.current !== requestId) return;
        const parsed = parseRecipe(view.body);
        setParsedConfig(parsed);
        setSourceYaml(view.body);
        const seeded = parsed.errors.length === 0 ? seedOverrideRows(parsed.data) : null;
        // 프리필 진입이 고른 config 면 팀 관례 시드 대신 원본 세션의 override 행을 쓴다(1회).
        // 그 사이 사용자가 다른 config 를 골랐으면 사용하지 않고 버린다.
        let prefillRows: KeyValuePair[] | null = null;
        if (!keepOverrides) {
          prefillRows =
            prefillOverrideRowsRef.current?.configPath === configName
              ? prefillOverrideRowsRef.current.rows
              : null;
          prefillOverrideRowsRef.current = null;
        }
        setForm((prev) =>
          learningCoreRef(prev) === ref && prev.config.configName === configName
            ? {
                ...prev,
                config: {
                  ...prev.config,
                  inlineYaml: view.body,
                  overrides: keepOverrides
                    ? prev.config.overrides
                    : (prefillRows ?? seeded ?? prev.config.overrides),
                },
              }
            : prev,
        );
      })
      .catch(() => {
        if (requestedConfigLoadRef.current === requestId) {
          setParsedConfig(null);
        }
      })
      .finally(() => {
        if (requestedConfigLoadRef.current === requestId) {
          setConfigBodyLoading(false);
        }
      });
  }, []);

  // config 선택. config 자체가 바뀌는 경로다. config 종속 노브(RandomBackground·해상도)를 되돌리고 본문을
  // 새로 받는다. 본문 전체가 바뀌므로 이전 본문 기준 편집은 의미가 없다. 알리지 않고 버리면 입력한
  // 내용이 사라진 이유를 알 수 없으므로, 버렸다는 사실만 표시해서 화면이 알린다. 직접 편집 중이었으면
  // overrides 방식으로 돌아오므로 비워 둔 행도 함께 복원한다. 그래야 본문이 도착할 때
  // `prefillRows ?? seeded ?? prev` 폴백이 빈 목록이 아니라 복원된 행을 읽는다.
  const selectConfig = useCallback(
    (configName: string) => {
      const snapshot = overridesBeforeDirectEditRef.current;
      overridesBeforeDirectEditRef.current = null;
      setForm((prev) => {
        setConfigEditDiscarded(hasConfigEdit(prev.config));
        const restored = resetSigmaConfigDefaults(restoreOverridesBeforeDirectEdit(prev, snapshot));
        return {
          ...restored,
          config: {
            ...restored.config,
            configName,
            inlineYaml: "",
            editedYaml: null,
            randomBackgroundEnabled: true,
            // 해상도는 config 종속 값이다. 이전 config 에서 바꾼 값이 새 config 로 넘어가서
            // 잘못된 override 로 전달되지 않도록 자동 입력 상태로 되돌린다.
            imageSize: null,
          },
        };
      });
      // ref 는 선택 시점에 동기로 기록되는 ref 에서 읽는다. 브랜치·커밋 자동/직접 선택 직후(리렌더 전)에
      // 호출되어도 최신 값을 읽는다.
      loadConfigBody(requestedLearningCoreRef.current, configName, false);
    },
    [loadConfigBody],
  );

  // learning-core ref(브랜치 이름 또는 고정 커밋)의 config 목록을 받고 하나를 자동 선택한다.
  // 브랜치 선택·커밋 고정·리비전 적용이 모두 이 경로다. `keepConfigPath` 는 리비전 전환이 전달하는
  // 현재 config 경로다(없으면 ""). 그 본문을 목록과 **병렬**로 받는다(§7). 목록은 그 경로가 새 ref 에도
  // 있는지 확인하는 용도라, 본문 요청이 목록 응답을 기다릴 필요가 없다. 목록에 그 경로가 없으면 기본
  // 선택을 쓴다(브랜치 전환의 프리필 이름 폴백과 같은 형태).
  const loadLearningCoreConfigs = useCallback(
    (ref: string, requestedKeepPath: string) => {
      requestedLearningCoreRef.current = ref;
      const cache = branchConfigsCacheRef.current;
      const cached = ref === "" ? undefined : cache.get(ref);
      let keepConfigPath = requestedKeepPath;
      if (keepConfigPath !== "" && cached !== undefined) {
        // 같은 트리다. 본문이 같으므로 목록 상태만 맞추고 요청과 편집본 폐기 없이 끝낸다. 본문이
        // 아직 없으면(로딩 중) 아래 일반 경로로 다시 받는다.
        if (cached.sha === loadedTreeShaRef.current && formRef.current.config.inlineYaml !== "") {
          setBranchConfigsState({ status: "success", data: cached });
          return;
        }
        // 캐시가 이미 "그 경로가 없다"고 알려 주면 불필요한 본문 요청을 보내지 않고 기본 선택을 쓴다.
        if (!cached.configs.some((entry) => entry.path === keepConfigPath)) {
          keepConfigPath = "";
        }
      }
      if (keepConfigPath === "") {
        loadConfigBody(ref, "", false);
      } else {
        // 같은 config 의 새 리비전. 본문이 바뀌므로 편집본은 버리고 알린다. override 행·노브는 그대로 둔다.
        const snapshot = overridesBeforeDirectEditRef.current;
        overridesBeforeDirectEditRef.current = null;
        setForm((prev) => {
          setConfigEditDiscarded(hasConfigEdit(prev.config));
          const restored = restoreOverridesBeforeDirectEdit(prev, snapshot);
          return { ...restored, config: { ...restored.config, inlineYaml: "", editedYaml: null } };
        });
        loadConfigBody(ref, keepConfigPath, true);
      }
      if (ref === "") {
        setBranchConfigsState({ status: "idle" });
        return;
      }
      // 목록이 준비되면 기본 config 를 자동 선택해서 본문·시드까지 미리 채운다. 사용자가 아무것도
      // 고르지 않아도 유효한 시작 상태가 되게 한다(브랜치 main 자동 선택과 같은 목적).
      //
      // finetune 중이면 그 기본은 finetune 레시피다. 여기서 구분하지 않으면 브랜치를 바꾸기만
      // 해도 시작점이 finetune 인 채 scratch 용 default 로 되돌아간다.
      const autoSelectConfig = (data: BranchConfigsView) => {
        if (requestedLearningCoreRef.current !== ref) {
          return;
        }
        // 유지할 경로가 새 ref 에도 있으면 선택을 그대로 둔다. 본문은 이미 병렬로 받는 중이다.
        if (keepConfigPath !== "" && data.configs.some((entry) => entry.path === keepConfigPath)) {
          return;
        }
        // 프리필 config 이름이 있으면(1회) 정규화 이름으로 목록 경로를 찾아 그 경로를 고른다.
        // 목록에 없으면 다른 레시피가 알림 없이 선택되지 않도록 알리고 기본 선택으로 폴백한다.
        const prefillName = prefillConfigNameRef.current;
        if (prefillName !== null) {
          prefillConfigNameRef.current = null;
          const prefillPath = findConfigPath(data.configs, prefillName);
          if (prefillPath !== undefined) {
            const rows = prefillRef.current?.overrideRows ?? null;
            if (rows !== null) {
              prefillOverrideRowsRef.current = { configPath: prefillPath, rows };
            }
            setFinetuneConfigNotice(null);
            selectConfig(prefillPath);
            return;
          }
          toast.info(
            `원본 config(${prefillName})를 Branch 목록에서 찾지 못해 기본 config 를 선택합니다.`,
          );
        }
        const resolved = resolveDefaultConfigPath(
          data.configs,
          formRef.current.init.strategy === "finetune",
        );
        setFinetuneConfigNotice(resolved.finetuneMissing ? finetuneConfigMissingMessage() : null);
        if (resolved.path !== undefined) {
          selectConfig(resolved.path);
        }
      };
      if (cached) {
        loadedTreeShaRef.current = cached.sha;
        setBranchConfigsState({ status: "success", data: cached });
        autoSelectConfig(cached);
        return;
      }
      setBranchConfigsState({ status: "loading" });
      listTrainingBranchConfigs(ref)
        .then((data) => {
          // 해석된 sha 로도 캐시한다. 브랜치 tip 을 그대로 고정하면 같은 트리라 다시 받을 필요가 없다.
          cache.set(ref, data);
          cache.set(data.sha, data);
          // 응답 도착 전에 다른 ref 로 바꿨으면 상태를 덮어쓰지 않는다(캐시에는 남겨 다시 방문할 때 쓴다).
          if (requestedLearningCoreRef.current === ref) {
            loadedTreeShaRef.current = data.sha;
            setBranchConfigsState({ status: "success", data });
            autoSelectConfig(data);
          }
        })
        .catch((error) => {
          if (requestedLearningCoreRef.current === ref) {
            // HTTP 오류는 서버 메시지를 유지한다. 내부 예외(then 체인 포함)는 raw 메시지를
            // UI 에 표시하지 않는다. 사용자는 드롭다운에서 수동으로 선택해 진행할 수 있다.
            setBranchConfigsState({
              status: "error",
              message: isHttpClientError(error)
                ? resolveLoadErrorMessage(error)
                : "config를 선택해 주세요.",
            });
            // 유지하려던 config 가 이 ref 에 있는지 확인하지 못했다. 이름만 남기면 목록 없이
            // 제출되어 BE 에서 거부된다. 이름을 비워 게이트를 닫고 사용자가 다시 고르게 한다.
            if (keepConfigPath !== "") {
              setForm((prev) => ({ ...prev, config: { ...prev.config, configName: "" } }));
              loadConfigBody(ref, "", false);
            }
          }
        });
    },
    [loadConfigBody, selectConfig],
  );

  // learning-core 출처(브랜치·커밋 고정) 전환의 단일 경로. 폼의 두 축과 requestedLearningCoreRef 가
  // 함께 바뀐다. `keepConfig` 가 꺼져 있으면 config 선택을 초기화한다(레시피는 브랜치 종속이고, override 행은
  // 다음 config 선택 때 시드로 대체된다). 켜져 있으면 현재 config 를 새 리비전에서 다시 받는다.
  const setLearningCore = useCallback(
    (next: Pick<SubmitFormState, "branchRef" | "commitPin">, keepConfig: boolean) => {
      const keepConfigPath = keepConfig ? formRef.current.config.configName : "";
      const snapshot = keepConfig ? null : overridesBeforeDirectEditRef.current;
      if (!keepConfig) overridesBeforeDirectEditRef.current = null;
      setForm((prev) => {
        if (keepConfig) {
          return { ...prev, ...next };
        }
        // 직접 편집 중이었으면 비워 둔 행을 먼저 복원한다.
        const restored = resetSigmaConfigDefaults(restoreOverridesBeforeDirectEdit(prev, snapshot));
        return {
          ...restored,
          ...next,
          config: { ...emptyConfigState(), overrides: restored.config.overrides },
        };
      });
      loadLearningCoreConfigs(learningCoreRef(next), keepConfigPath);
    },
    [loadLearningCoreConfigs],
  );

  // 브랜치 선택. 커밋 고정은 해제된다. 고정은 브랜치 위에 추가한 값이라 브랜치가 바뀌면 뜻이 없다.
  // 같은 브랜치를 다시 고르면 아무것도 하지 않는다. c6 드롭다운은 이미 고른 항목을 눌러도
  // onValueChange 를 호출하므로, 여기서 걸러야 고정과 config 가 알림 없이 해제되지 않는다.
  const selectBranch = useCallback(
    (branchRef: string) => {
      if (branchRef !== formRef.current.branchRef) {
        setLearningCore({ branchRef, commitPin: null }, false);
      }
    },
    [setLearningCore],
  );

  // 커밋 고정 전환. `null` 은 브랜치 최신으로 되돌린다.
  const selectCommit = useCallback(
    (sha: string | null) => {
      const current = formRef.current;
      if (current.commitPin !== sha) {
        setLearningCore({ branchRef: current.branchRef, commitPin: sha }, true);
      }
    },
    [setLearningCore],
  );

  // 브랜치와 커밋을 한 번에 설정한다. Policy 제안 적용과 재제출 프리필의 경로다. `ref` 가 null 이면
  // 브랜치는 현재 값을 유지한다(브랜치를 모르는 40-hex 제출·출처 브랜치가 없는 Policy).
  const applyLearningCoreRevision = useCallback(
    (ref: string | null, sha: string) =>
      setLearningCore({ branchRef: ref ?? formRef.current.branchRef, commitPin: sha }, true),
    [setLearningCore],
  );

  // 커밋 목록. 브랜치가 정해지면 받는다. selectBranch 의 config 요청과 같은 틱에 전송되어 병렬이다.
  // fine-tune 이고 base Policy 가 **이 브랜치**에서 학습되었으면 `since=<그 sha>` 구간 조회로 "Policy
  // 학습 시점 이후 커밋"을 받는다. 다른 브랜치에 since 를 적용하면 `since_reached=false` 를 "창 밖"으로
  // 잘못 해석하므로 브랜치가 같을 때만 적용한다. 브랜치 없이 커밋만 고정된 상태는 목록을 받지 않는다(합성
  // 옵션만 표시한다).
  const finetunePolicy = form.init.strategy === "finetune" ? form.init.policy : null;
  // 제출 pin 은 40-hex 만 받으므로 그 외 provenance 는 제안하지 않는다(BE 도 since 를 422 로 거절한다).
  const policySha =
    finetunePolicy?.learningCoreSha != null && isFullSha(finetunePolicy.learningCoreSha)
      ? finetunePolicy.learningCoreSha.toLowerCase()
      : null;
  // Policy 가 커밋 고정 세션에서 등록되었으면 ref 가 sha 다. 브랜치 값으로 넣지 않는다(프리필의
  // learningCoreRevision 과 같은 판정). 그때 제안은 브랜치를 유지하고 커밋만 고정한다.
  const policyRef =
    finetunePolicy?.learningCoreRef && !isFullSha(finetunePolicy.learningCoreRef)
      ? finetunePolicy.learningCoreRef
      : null;
  const commitsSince = policySha !== null && policyRef === form.branchRef ? policySha : null;
  const commitsBranchRef = form.branchRef;
  const commitPinEnabled = features.commitPin;
  useEffect(() => {
    // Commit 을 고정할 수 없는 위저드에서는 아무도 이 목록을 그리지 않는다. 그래서 브랜치를 고를 때마다
    // 커밋 200개를 받는 왕복을 모두 뺀다(rules §7). 상태를 `idle` 로 두어야 한다. `success`
    // 로 꾸미면 목록이 다시 필요해졌을 때 빈 목록을 사실처럼 그린다.
    if (commitsBranchRef === "" || !commitPinEnabled) {
      setCommitsState({ status: "idle" });
      return;
    }
    const cache = branchCommitsCacheRef.current;
    const key = `${commitsBranchRef}|${commitsSince ?? ""}`;
    let cached = cache.get(key);
    // 브랜치 기본 목록에 그 커밋이 이미 있으면 구간을 로컬에서 자른다. 왕복 하나와 드롭다운
    // 잠김 한 번을 줄인다. 창 밖일 때만 BE 에 조회한다.
    if (cached === undefined && commitsSince !== null) {
      const base = cache.get(`${commitsBranchRef}|`);
      const at = base?.commits.findIndex((commit) => commit.sha === commitsSince) ?? -1;
      if (base !== undefined && at >= 0) {
        cached = {
          ...base,
          since: commitsSince,
          since_reached: true,
          commits: base.commits.slice(0, at + 1),
        };
        cache.set(key, cached);
      }
    }
    if (cached !== undefined) {
      setCommitsState({ status: "success", data: cached });
      return;
    }
    // 키가 바뀌면 cleanup 이 먼저 실행되어 늦은 응답을 버린다. 캐시로 되돌아온 경우도 같다.
    let disposed = false;
    setCommitsState({ status: "loading" });
    listTrainingBranchCommits(
      commitsBranchRef,
      commitsSince === null ? {} : { since: commitsSince, limit: BRANCH_COMMITS_SINCE_LIMIT },
    )
      .then((data) => {
        cache.set(key, data);
        if (!disposed) {
          setCommitsState({ status: "success", data });
        }
      })
      .catch((error) => {
        if (!disposed) {
          setCommitsState({ status: "error", message: resolveLoadErrorMessage(error) });
        }
      });
    return () => {
      disposed = true;
    };
  }, [commitPinEnabled, commitsBranchRef, commitsSince]);

  // "Branch 최신"이 지금 가리키는 커밋. 우선순위:
  //   1. 커밋 목록의 head(정본).
  //   2. 같은 브랜치로 받은 config 목록이 해석한 sha.
  //   3. submit-options 의 브랜치 sha.
  // Commit 드롭다운의 센티널 라벨과 확정 요약이 같은 값을 쓴다.
  const branchTipSha =
    form.branchRef === ""
      ? null
      : commitsState.status === "success" && commitsState.data.ref === form.branchRef
        ? commitsState.data.sha
        : branchConfigsState.status === "success" && branchConfigsState.data.ref === form.branchRef
          ? branchConfigsState.data.sha
          : optionsState.status === "success"
            ? (optionsState.data.branches.find((branch) => branch.name === form.branchRef)?.sha ??
              null)
            : null;

  // fine-tune 제안. base Policy 가 학습된 커밋이 있고 **제출될 커밋**과 다를 때만 표시한다. 고정이 그 sha 이거나
  // 브랜치 최신이 곧 그 sha 이면 어긋난 것이 없다. 적용은 사용자가 누른다.
  const submittedSha = form.commitPin ?? branchTipSha;
  const finetuneRevisionSuggestion =
    policySha !== null && submittedSha !== policySha ? { ref: policyRef, sha: policySha } : null;

  // 시작점 전환. finetune 은 두 가지를 함께 바꾼다. 리소스를 1×1 로 줄이고(BE 는 그 외에
  // 400), config 를 finetune 기본 레시피로 바꾼다. retrain 은 리소스만 줄인다. 소스 세션의
  // 레시피로 다시 실행하는 것이 기본이라 config 를 교체하지 않는다.
  const setInitStrategy = useCallback(
    (strategy: SubmitInitState["strategy"]) => {
      // 이 위저드가 열지 않는 시작점이면 아무것도 하지 않는다. 패널이 라디오를 잠그지만,
      // 정책이 렌더 경로에만 있으면 다른 호출부가 생기는 순간 그 값이 전달된다.
      if (initStrategyLockReason(features, strategy) !== null) {
        return;
      }
      if (strategy === "scratch") {
        // 되돌릴 때 리소스 값은 복원하지 않는다. 잠금만 풀고 사용자가 다시 정한다.
        setForm((prev) => ({ ...prev, init: { strategy: "scratch" } }));
        setFinetuneConfigNotice(null);
        // config 는 되돌린다. finetune 진입이 교체한 레시피를 그대로 두면 이름·오른쪽 본문·
        // override 시드가 finetune 상태로 scratch 제출된다.
        if (branchConfigsState.status !== "success") {
          // 목록이 아직 없으면, 목록이 도착할 때 selectBranch 의 자동 선택이 scratch 기준으로 판정한다.
          return;
        }
        const scratchDefault = resolveDefaultConfigPath(branchConfigsState.data.configs, false);
        if (
          scratchDefault.path !== undefined &&
          formRef.current.config.configName !== scratchDefault.path
        ) {
          selectConfig(scratchDefault.path);
        }
        return;
      }
      if (strategy === "retrain") {
        // 소스가 없으면 고를 수 없는 축이다(패널이 비활성으로 막지만 여기서도 막는다).
        if (retrainSource === null) {
          return;
        }
        setForm((prev) => ({
          ...prev,
          init: { strategy: "retrain", source: retrainSource },
          nodes: 1,
          gpusPerNode: 1,
        }));
        setFinetuneConfigNotice(null);
        return;
      }
      setForm((prev) => ({
        ...prev,
        init: { strategy: "finetune", policy: null },
        nodes: 1,
        gpusPerNode: 1,
      }));
      if (branchConfigsState.status !== "success") {
        // 목록이 아직 없으면 "없다"고 단정하지 않는다. 목록이 도착할 때 selectBranch 의 자동 선택이
        // finetune 기준으로 다시 판정한다.
        setFinetuneConfigNotice(null);
        return;
      }
      const resolved = resolveDefaultConfigPath(branchConfigsState.data.configs, true);
      setFinetuneConfigNotice(resolved.finetuneMissing ? finetuneConfigMissingMessage() : null);
      // 브랜치에 기본 레시피가 없으면 현재 선택을 유지한다. 임의 config 로 바꾸는 것이 더 나쁘다.
      if (resolved.path !== undefined && formRef.current.config.configName !== resolved.path) {
        selectConfig(resolved.path);
      }
    },
    [branchConfigsState, features, retrainSource, selectConfig],
  );

  // 시작점 Policy 확정. finetune 이 아니면 no-op 이다. 전략 전환은 setInitStrategy 가 소유한다.
  const setInitPolicy = useCallback((policy: SubmitInitPolicy) => {
    setForm((prev) =>
      prev.init.strategy === "finetune"
        ? { ...prev, init: { strategy: "finetune", policy } }
        : prev,
    );
  }, []);

  // 시작점 후보 목록. finetune 을 켠 동안에만, 스코프가 바뀔 때 받는다. 모듈 캐시가 있어서
  // 스코프를 오가도 다시 요청하지 않는다.
  const initPolicyEnabled = form.init.strategy === "finetune";
  useEffect(() => {
    if (!initPolicyEnabled) {
      return;
    }
    let cancelled = false;
    setInitPolicyState({ status: "loading" });
    loadScopedPolicies({ scope: initPolicyScope })
      .then((policies) => {
        if (!cancelled) {
          setInitPolicyState({ status: "success", data: policies });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setInitPolicyState({ status: "error", message: resolveLoadErrorMessage(error) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [initPolicyEnabled, initPolicyScope]);

  useEffect(() => {
    let cancelled = false;
    setOptionsState({ status: "loading" });
    getTrainingSubmitOptions()
      .then((options) => {
        if (cancelled) {
          return;
        }
        setOptionsState({ status: "success", data: options });
        // 리소스 기본값을 폼에 시드한다. 프리필(원본 세션의 자원 스냅샷)이 있으면 프리필이 우선이다.
        setForm((prev) => ({
          ...prev,
          nodes: prefillRef.current?.nodes ?? options.resource_defaults.nodes,
          gpusPerNode: prefillRef.current?.gpusPerNode ?? options.resource_defaults.gpus_per_node,
        }));
        // 기본 브랜치(main, 없으면 첫 브랜치)를 자동 선택해서 창을 연 직후 config 목록을 미리
        // 적재한다. 사용자가 실험 설정 스텝에 있는 동안 구성 스텝 데이터가 준비된다.
        // 원본이 실행한 커밋을 알면 그 커밋으로 고정해 연다. 재제출이 같은 코드로 재현되고, 브랜치가
        // 그 사이 없어졌어도 config 는 sha 로 받는다(커밋 목록만 실패하고 화면이 브랜치 부재를 표시한다).
        // 커밋을 모르면 프리필 브랜치를 고른다. 브랜치가 없어졌으면 config 목록 로드가 눈에 보이게
        // 실패하고 사용자가 다시 고른다(알림 없는 폴백 없음). 브랜치를 모르는 40-hex 제출은 브랜치를
        // 비우고 커밋만 고정한다.
        const prefillBranch = prefillRef.current?.branchRef ?? null;
        const prefillSha = prefillRef.current?.commitSha ?? null;
        const defaultBranch =
          options.branches.find((branch) => branch.name === "main") ?? options.branches[0];
        if (learningCoreRef(formRef.current) === "") {
          if (prefillSha !== null) {
            applyLearningCoreRevision(prefillBranch, prefillSha);
          } else if (prefillBranch !== null) {
            selectBranch(prefillBranch);
          } else if (defaultBranch) {
            selectBranch(defaultBranch.name);
          }
        }
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        setOptionsState({
          status: "error",
          message: isNoTrainingTeamError(error) ? NO_TEAM_MESSAGE : resolveLoadErrorMessage(error),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [applyLearningCoreRevision, selectBranch]);

  const updateForm = useCallback((patch: Partial<SubmitFormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  }, []);

  // 선택 구성이 같은지. 순서도 비교한다. 순서가 곧 타깃 인덱스라 재배열도 구성 변경이다.
  const datasetMembershipKey = (datasets: readonly SubmitDatasetSelection[]): string =>
    datasets.map((dataset) => dataset.uuid).join("|");

  // 선택 목록 전체를 교체한다. 구성이 바뀌면 채널 선택을 초기화한다. 교집합이 달라져서 이전
  // 선택이 없는 채널을 가리킬 수 있고, 새 교집합 기준 제안으로 다시 시드된다.
  const setDatasets = useCallback((next: SubmitDatasetSelection[]) => {
    // 입력이 하나로 줄면 역할을 train 으로 고정한다. 목록이 바뀌는 입구가 여기 하나뿐이라
    // picker 토글과 카드 제거가 같은 규칙을 따른다.
    // 같은 Dataset 을 두 번 담으면 세트 선택도 한 값으로 합친다. mask 자리가 Dataset 당 하나뿐이라
    // BE 는 서로 다른 세트를 422 로 거부한다(그 전에 값이 달라지지 않게 한다).
    const datasets = withSyncedAnnotationSets(withLockedRole(next));
    // 사용자가 목록을 바꿨으면 "승계한 세트를 비웠다" 안내는 더 이상 필요 없다. 다시 고른 뒤에도
    // 남아 있으면 방금 고른 값이 또 비워진 것처럼 보인다.
    setDroppedAnnotationSetCount(0);
    setForm((prev) => {
      const sameMembers = datasetMembershipKey(prev.datasets) === datasetMembershipKey(datasets);
      return {
        ...prev,
        datasets,
        channelSelection: sameMembers ? prev.channelSelection : null,
        sigmaDefaults:
          sameMembers || prev.sigmaDefaults === undefined
            ? prev.sigmaDefaults
            : { ...prev.sigmaDefaults, suggestionReady: false },
        // 타깃은 배열 위치라 구성이 바뀌면 가리키던 대상이 달라진다.
        targetDatasetIndex: sameMembers ? prev.targetDatasetIndex : null,
      };
    });
  }, []);

  const datasetKey = form.datasets.map((dataset) => dataset.uuid).join("|");
  // 선택 데이터셋들의 채널 구조(topic→채널·dim·arm_layout). 매핑 UI 와 미리보기 카드의 소스다.
  // 학습이 N개를 한 배치로 섞으므로 매핑 후보는 이 구조들의 교집합이다. 적재 실패는 제출을 막지
  // 않는다(BE 가 제출할 때 재조립한다). 안내만 남긴다.
  const structureState = usePerDatasetLoad(datasetKey, getDatasetStructure, structureUuid);
  // 선택 데이터셋의 상세. Isaac Evaluation의 `environment=simulation` 라벨과 sigma 기본값의
  // embodiment를 읽는다. 선택 모델(SubmitDatasetSelection)은 이 정보를 가지지 않고 진입점이 여럿이라
  // (picker·데이터셋 표면·재제출 prefill) 컨트롤러가 uuid 로 받는다. 구조와 같은 커밋에서 전송되므로
  // 왕복이 늘지 않는다. 받지 못하면 평가 섹션과 sigma 기본값을 건너뛰고 제출은 막지 않는다.
  const datasetDetailsState = usePerDatasetLoad(datasetKey, getDataset, datasetUuid);
  const trainingDatasets = useMemo(
    () => form.datasets.filter((dataset) => dataset.role === "train"),
    [form.datasets],
  );
  const trainingDatasetNames = useMemo(
    () => trainingDatasets.map((dataset) => dataset.label),
    [trainingDatasets],
  );
  const trainingDatasetEmbodiments = useMemo(() => {
    if (trainingDatasets.length === 0) return [];
    if (datasetDetailsState.status !== "success") return undefined;
    const byUuid = new Map(
      datasetDetailsState.data.map((dataset) => [dataset.uuid, dataset.embodiment.name]),
    );
    const embodiments = trainingDatasets.map((dataset) => byUuid.get(dataset.uuid));
    return embodiments.every((name) => name !== undefined) ? embodiments : undefined;
  }, [trainingDatasets, datasetDetailsState]);
  const isaacEvaluationParams = useEvalImageParams(
    trainingDatasetNames,
    trainingDatasetEmbodiments,
  );

  const sigmaDefaultsEnabled = prefill === undefined;
  const sigmaDatasetKey =
    sigmaDefaultsEnabled &&
    datasetDetailsState.status === "success" &&
    isSigmaDatasetSelection(
      form.datasets.map((dataset) => dataset.uuid),
      datasetDetailsState.data,
    )
      ? datasetKey
      : "";
  // /structure의 채널 종류와 무관하게 /details의 영상별 실측 해상도를 사용한다.
  const cameraDetailsState = usePerDatasetLoad(
    sigmaDatasetKey,
    getDatasetDetails,
    cameraDetailsUuid,
  );
  const datasetDefaults = useMemo(
    () =>
      sigmaDatasetDefaults(
        sigmaDefaultsEnabled ? form.datasets.map((dataset) => dataset.uuid) : [],
        datasetDetailsState.status === "success" ? datasetDetailsState.data : [],
        cameraDetailsState.status === "success" ? cameraDetailsState.data : [],
      ),
    [sigmaDefaultsEnabled, form.datasets, datasetDetailsState, cameraDetailsState],
  );

  const sigmaDefaultsLoading =
    sigmaDefaultsEnabled &&
    form.datasets.length > 0 &&
    (datasetDetailsState.status === "idle" ||
      datasetDetailsState.status === "loading" ||
      (datasetDefaults.applicable &&
        (configBodyLoading ||
          cameraDetailsState.status === "idle" ||
          cameraDetailsState.status === "loading" ||
          (sourceYaml !== "" &&
            form.channelSelection === null &&
            !form.sigmaDefaults?.suggestionReady))));

  useEffect(() => {
    if (!sigmaDefaultsEnabled || configBodyLoading) return;
    setForm((prev) =>
      prev.datasets.map((dataset) => dataset.uuid).join("|") === datasetKey
        ? reconcileSigmaDefaults(prev, datasetDefaults)
        : prev,
    );
  }, [
    sigmaDefaultsEnabled,
    configBodyLoading,
    datasetKey,
    datasetDefaults,
    form.config,
    form.channelSelection,
    form.sigmaDefaults,
  ]);

  const sigmaDefaultsNotice =
    !sigmaDefaultsEnabled || form.datasets.length === 0
      ? null
      : datasetDetailsState.status === "error"
        ? "Dataset 정보를 확인하지 못해 sigma_hand_1.2 기본값을 적용하지 않았습니다."
        : datasetDefaults.applicable
          ? [
              "sigma_hand_1.2 기본값: 입력 해상도 238 × 294, wrist_lower·wrist_upper에 Dataset 해상도 적용, wrist_left·wrist_right 제거, observer 해제. 적용 후 직접 수정할 수 있습니다.",
              ...datasetDefaults.warnings,
              ...(cameraDetailsState.status === "error"
                ? ["카메라 해상도를 확인하지 못해 wrist 해상도는 기존 설정을 유지합니다."]
                : []),
            ].join(" ")
          : null;

  // 입력별 어노테이션 세트 후보. 담는 순간 카드별로 1회 조회한다. 구조·상세와 같은 훅을 쓰므로
  // 목록을 오가도 다시 조회하지 않고, 한쪽 실패가 다른 쪽을 실패시키지 않는다. 실패·로딩은 제출을
  // 막지 않는다. 세트는 선택 사항이고 마지막 게이트는 BE 가 가진다.
  const annotationSetsState = usePerDatasetLoad(
    datasetKey,
    fetchAnnotationSets,
    annotationSetsUuid,
  );
  const annotationSetsByDataset = useMemo(() => {
    const byDataset = new Map<string, readonly AnnotationSetSummaryView[]>();
    if (annotationSetsState.status === "success") {
      for (const entry of annotationSetsState.data) {
        byDataset.set(entry.dataset_uuid, entry.sets);
      }
    }
    return byDataset;
  }, [annotationSetsState]);

  const annotationSets: AnnotationSetsSource = useMemo(
    () => ({ status: annotationSetsState.status, byDataset: annotationSetsByDataset }),
    [annotationSetsState.status, annotationSetsByDataset],
  );

  // 프리필이 승계한 세트를 지금도 쓸 수 있는지는 후보를 받은 뒤에만 알 수 있다(삭제되었거나 다시
  // 렌더 중일 수 있다). 쓸 수 없는 세트는 비우고 건수만 남긴다. 그대로 두면 확정 단계에서 404/409
  // 오류가 난다. 건수는 누적하지 않고 이번 판정 결과로 덮어쓴다(effect 재실행에 값이 커지지 않게 한다).
  useEffect(() => {
    if (annotationSetsState.status !== "success") {
      return;
    }
    // 후보를 받지 못한 입력은 판정하지 않는다. 모른다는 이유로 지우면 사용자 선택이 사라진다.
    const unusable = new Set(
      formRef.current.datasets
        .filter((dataset) => {
          const sets = annotationSetsByDataset.get(dataset.uuid);
          return (
            dataset.annotationSetUuid !== null &&
            sets !== undefined &&
            !sets.some(
              (set) => set.uuid === dataset.annotationSetUuid && isAnnotationSetSelectable(set),
            )
          );
        })
        .map((dataset) => dataset.uuid),
    );
    if (unusable.size === 0) {
      return;
    }
    setDroppedAnnotationSetCount(unusable.size);
    setForm((prev) => ({
      ...prev,
      datasets: prev.datasets.map((dataset) =>
        unusable.has(dataset.uuid) ? { ...dataset, annotationSetUuid: null } : dataset,
      ),
    }));
  }, [annotationSetsByDataset, annotationSetsState.status]);

  // 시뮬레이션 데이터셋이 하나라도 있어야 Isaac Evaluation 을 고를 수 있다. 모르는 동안(로딩·실패)에는
  // 잠근다. 열렸다가 잠기는 것보다 늦게 열리는 것이 사용자에게 덜 혼란스럽다. 권한이 없으면 잠금이
  // 아니라 섹션을 숨긴다(사용자 결정). 자격 안내는 Evaluation 메뉴처럼 노출하지 않는다.
  const simulationReady =
    datasetDetailsState.status === "success" && hasSimulationDataset(datasetDetailsState.data);
  const isaacEvaluationAvailable = canUseIsaacEvaluation && simulationReady;
  const isaacEvaluationEnabled = isaacEvaluationEnabledRaw && isaacEvaluationAvailable;
  const isaacEvaluationPlan = resolveIsaacEvaluationPlan(
    isaacEvaluationEnabled,
    isaacEvaluationParams,
  );

  // 편집본이 있으면 편집본을 파싱해서 계열 감지·override 후보·auto-LR 이 실제 제출될 본문을 따르게
  // 한다. 편집이 없으면 config 선택 때 받은 파싱 결과를 그대로 쓴다(재파싱 없음).
  // 텍스트가 같으면 다시 파싱하지 않는다. 정규화 비교는 YAML 왕복이라 타이핑마다 실행하기에는 비싸다.
  const editedYaml = form.config.editedYaml;
  const activeParsedConfig = useMemo(() => {
    if (editedYaml === null || editedYaml === form.config.inlineYaml) {
      return parsedConfig;
    }
    return parseRecipe(editedYaml);
  }, [editedYaml, form.config.inlineYaml, parsedConfig]);

  // 편집본을 YAML 로 읽을 수 없으면 그 자리에서 알리고 '다음'을 막는다. 레포 원본의 파싱 실패는
  // 여기서 판정하지 않는다. 편집하지 않은 config 는 이름만으로 제출할 수 있어야 한다
  // (본문 파싱은 override 후보를 좁히는 보조 수단이고 제출 조건이 아니다).
  const configParseErrors = hasConfigEdit(form.config) ? (activeParsedConfig?.errors ?? []) : [];
  const configEditInvalid = configParseErrors.length > 0;

  // 세트 선택과 mask co-train 스위치가 어긋났는지. 편집본까지 반영한 config 로 판정한다(override 가
  // 본문보다 우선한다). 제출을 막지 않고 안내 문구로만 연결한다.
  const annotationMaskNotice =
    maskGenerationBlocked ??
    annotationMaskAdvice({
      datasets: form.datasets,
      overrides: form.config.overrides,
      configBody: activeParsedConfig?.data ?? null,
    });

  // 채널 선택 제안 시드: (데이터셋, 브랜치, config 본문)이 모두 있으면 BE preview 를 1회 호출해서
  // suggested_selection 으로 시드한다. 실패해도 부작용이 없다. 선택이 null 이면 BE 가 제출할 때
  // 기본 제안으로 재조립한다.
  const seededKeyRef = useRef("");
  const seedDatasetKeyRef = useRef(datasetKey);
  const configLoaded = sourceYaml !== "";
  // 시드 키는 제출될 ref(고정 커밋 또는 브랜치)를 쓴다. 브랜치 이름만 쓰면 커밋만 고정된
  // 재제출에서 시드가 실행되지 않는다.
  const activeLearningCoreRef = learningCoreRef(form);
  useEffect(() => {
    // 빈 선택을 거쳐 같은 Dataset을 다시 담아도 초기화된 채널 선택을 새로 채워야 한다.
    if (seedDatasetKeyRef.current !== datasetKey) {
      seedDatasetKeyRef.current = datasetKey;
      seededKeyRef.current = "";
    }
    if (datasetKey === "" || activeLearningCoreRef === "" || form.config.configName === "") {
      return;
    }
    if (!configLoaded || form.channelSelection !== null) {
      return;
    }
    const seedKey = `${datasetKey}|${activeLearningCoreRef}|${form.config.configName}`;
    if (seededKeyRef.current === seedKey) {
      return;
    }
    seededKeyRef.current = seedKey;
    const applySuggestion = (selection: ChannelSelection | null) =>
      setForm((prev) => {
        if (
          prev.datasets.map((dataset) => dataset.uuid).join("|") !== datasetKey ||
          learningCoreRef(prev) !== activeLearningCoreRef ||
          prev.config.configName !== form.config.configName
        )
          return prev;
        if (!sigmaDefaultsEnabled && (prev.channelSelection !== null || selection === null))
          return prev;
        const defaultSelection =
          prev.channelSelection === null &&
          selection?.observation_image?.includes("observer") &&
          prev.datasets.some((dataset) => /benchmark/i.test(dataset.label)) &&
          prev.sigmaDefaults?.observer !== true
            ? toggleSelectedChannel(selection, "observation_image", "observer")
            : selection;
        return {
          ...prev,
          channelSelection: prev.channelSelection ?? defaultSelection,
          sigmaDefaults: sigmaDefaultsEnabled
            ? { ...(prev.sigmaDefaults ?? emptySigmaDefaultsState()), suggestionReady: true }
            : prev.sigmaDefaults,
        };
      });
    previewTrainingOverrides({
      ...buildPreviewPayload(formRef.current),
      channel_selection: null,
    })
      .then((preview) => {
        // 3-상태 해석(부재 topic=위임 유지, 모든 topic 부재=시드 없음)은 wire 계약 경계
        // (api/channelSelection)가 소유한다. 여기서 배열로 합치면 위임이 명시적 빈
        // 선택으로 바뀌어 config 원본 mux 가 그대로 제출된다.
        const selection = channelSelectionFromSuggestion(preview.suggested_selection);
        applySuggestion(selection);
      })
      .catch(() => {
        // 시드가 실패하면 다음 트리거에서 다시 시도할 수 있도록 키를 되돌린다.
        if (seededKeyRef.current === seedKey) {
          seededKeyRef.current = "";
        }
        if (sigmaDefaultsEnabled) applySuggestion(null);
      });
  }, [
    datasetKey,
    activeLearningCoreRef,
    form.config.configName,
    form.channelSelection,
    configLoaded,
    sigmaDefaultsEnabled,
  ]);

  // 매핑 UI 의 채널 토글. 시드 전(선택 전체 null)에는 no-op 이다(에디터가 비활성 안내를 담당한다).
  // topic 별 3-상태 수정 규칙(위임 승격·빈 배열 유지)은 wire 계약 경계가 소유한다.
  const toggleChannel = useCallback((topic: ChannelSelectionTopic, name: string) => {
    setForm((prev) =>
      prev.channelSelection === null
        ? prev
        : {
            ...prev,
            channelSelection: toggleSelectedChannel(prev.channelSelection, topic, name),
            sigmaDefaults:
              prev.sigmaDefaults !== undefined &&
              topic === "observation_image" &&
              name === "observer"
                ? {
                    ...prev.sigmaDefaults,
                    observer: !prev.channelSelection.observation_image?.includes(name),
                  }
                : prev.sigmaDefaults,
          },
    );
  }, []);

  // 확정 스텝 미리보기. 제출과 같은 재조립 함수를 거친 "실제 주입될 overrides" 다.
  const [confirmPreviewState, setConfirmPreviewState] = useState<LoadState<OverridePreview>>({
    status: "idle",
  });
  // 확정 스텝 config 경고. 결과는 제출을 막지 않는 참고 신호다. 다만 결과가 도착하기 전에는 제출을 잠근다.
  // 경고보다 먼저 제출되면 사용자가 확실한 크래시(override_key_missing)를 보지 못한 채 GPU 로 보낸다.
  // POST 라 재시도가 없고, client timeout(30초)에서 error 로 해제된다. cross-region 왕복이라 확정
  // 스텝 진입과 Dataset 자동값 변경 때 호출하고 키 입력마다 호출하지 않는다.
  const [confirmValidationState, setConfirmValidationState] = useState<
    LoadState<TrainingValidationReport>
  >({ status: "idle" });
  const confirmPreviewRequestRef = useRef(0);
  // 두 요청은 응답 시각이 달라서 순서 판정을 각자 한다.
  const confirmValidationRequestRef = useRef(0);
  // 확정 화면에 들어온 뒤 늦게 도착한 Dataset 정보도 미리보기와 검증에 반영한다.
  const confirmationDefaultsKey = sigmaDefaultsEnabled
    ? JSON.stringify(form.sigmaDefaults)
    : undefined;
  useEffect(() => {
    if (step !== "confirm") {
      return;
    }
    const previewRequestId = ++confirmPreviewRequestRef.current;
    const validationRequestId = ++confirmValidationRequestRef.current;
    if (sigmaDefaultsLoading) {
      setConfirmPreviewState({ status: "loading" });
      setConfirmValidationState({ status: "loading" });
      return;
    }
    const current = formRef.current;
    if (
      current.datasets.length === 0 ||
      learningCoreRef(current) === "" ||
      current.config.configName === ""
    ) {
      setConfirmPreviewState({ status: "idle" });
      setConfirmValidationState({ status: "idle" });
      return;
    }
    setConfirmPreviewState({ status: "loading" });
    previewTrainingOverrides(buildPreviewPayload(current))
      .then((preview) => {
        if (confirmPreviewRequestRef.current === previewRequestId) {
          setConfirmPreviewState({ status: "success", data: preview });
        }
      })
      .catch((error) => {
        if (confirmPreviewRequestRef.current === previewRequestId) {
          setConfirmPreviewState({ status: "error", message: resolveLoadErrorMessage(error) });
        }
      });

    setConfirmValidationState({ status: "loading" });
    validateTrainingSession(buildValidatePayload(current))
      .then((report) => {
        if (confirmValidationRequestRef.current === validationRequestId) {
          setConfirmValidationState({ status: "success", data: report });
        }
      })
      .catch((error) => {
        // 경고 검사 실패는 제출과 관계없다. 서버가 제출할 때 같은 검사를 로그로 남긴다.
        if (confirmValidationRequestRef.current === validationRequestId) {
          setConfirmValidationState({ status: "error", message: resolveLoadErrorMessage(error) });
        }
      });
  }, [step, sigmaDefaultsLoading, confirmationDefaultsKey]);

  // finetune·retrain 에서 금지된 override 키. BE 도 제출 경계(400)에서 잡지만, 키를 입력한
  // 자리(구성 스텝)에서 바로 알리는 것이 되돌리기 쉽다.
  const reservedInitOverrides =
    form.init.strategy === "scratch"
      ? []
      : reservedInitOverrideKeys(effectiveConfigOverridePairs(form.config));

  // 재학습은 소스 세션의 config 를 그대로 사용해서 열린다. finetune 기본 레시피와 다를 때만
  // 그 사실을 표시한다. 같은 레시피면 알릴 내용이 없다.
  const retrainConfigNotice =
    form.init.strategy === "retrain" &&
    branchConfigsState.status === "success" &&
    form.config.configName !== findConfigPath(branchConfigsState.data.configs, FINETUNE_CONFIG_NAME)
      ? `원본 Session의 config 를 그대로 씁니다. finetune 기본 레시피로 바꾸려면 ${FINETUNE_CONFIG_NAME} 을 골라 주세요.`
      : null;

  // 내 학습 스코프를 폼에 고정하고 그 안의 실험을 적재한다. 화면에 워크스페이스 선택이 없으므로
  // (docs/rules.md §15) 마운트할 때 한 번만 호출한다. 하지만 workspace_id 는 제출 payload 값이라
  // 폼에는 남는다.
  // 알림 값 시드. 내 수신자 행이 정본이라 스코프가 정해질 때 한 번 읽는다. 실험 적재와
  // 병렬이라 순차 왕복이 늘지 않는다. 읽지 못하면 끈 상태로 둔다(키가 빠져서 상류 기본에 맡긴다).
  const seedNotifyOnFinish = useCallback(
    (workspaceId: string) => {
      if (selfNotificationEmail === null || selfNotificationEmail === "") {
        return;
      }
      void getWorkspaceNotifications(workspaceId)
        .then((view) => {
          const on = isSelfNotificationOn(view, selfNotificationEmail);
          setForm((prev) =>
            prev.workspaceId === workspaceId ? { ...prev, notifyOnFinish: on } : prev,
          );
        })
        .catch(() => {
          // 알림은 보조 값이라 실패를 화면에 표시하지 않는다. 꺼진 상태로 제출할 수 있다.
        });
    },
    [selfNotificationEmail],
  );

  const experimentsRequestId = useRef(0);
  const applyWorkspaceScope = useCallback(
    (workspaceId: string) => {
      setForm((prev) => ({ ...prev, workspaceId, experimentId: "", experimentName: null }));
      seedNotifyOnFinish(workspaceId);
      const requestId = ++experimentsRequestId.current;
      setExperimentsState({ status: "loading" });
      (features.ownership === "shared" ? listSharedExperiments() : listMyExperiments())
        .then((items) => {
          if (requestId !== experimentsRequestId.current) return;
          const scopedItems = items.filter((item) => item.workspace_id === workspaceId);
          setExperimentsState({ status: "success", data: scopedItems });
          // 첫 실험 자동 선택. 같은 스코프가 유지되고 아직 선택하지 않았을 때만 한다.
          const first = scopedItems[0];
          if (first) {
            setForm((prev) =>
              prev.workspaceId === workspaceId && prev.experimentId === ""
                ? { ...prev, experimentId: first.id, experimentName: first.name }
                : prev,
            );
          }
        })
        .catch((error) => {
          if (requestId !== experimentsRequestId.current) return;
          setExperimentsState({ status: "error", message: resolveLoadErrorMessage(error) });
        });
    },
    [features.ownership, seedNotifyOnFinish],
  );

  const selectExperiment = useCallback(
    (experimentId: string) => {
      const name =
        experimentsState.status === "success"
          ? (experimentsState.data.find((item) => item.id === experimentId)?.name ?? null)
          : null;
      // 다른 실험으로 바꾸면 편집 중이던 노트는 버린다. 남아 있으면 그 초안이 새 실험의
      // 노트로 저장된다.
      setExperimentNote(null);
      setForm((prev) => ({ ...prev, experimentId, experimentName: name }));
    },
    [experimentsState],
  );

  // 실험 노트 편집(열기/입력/되돌리기/저장). 저장이 성공하면 목록 캐시를 직접 갱신해서
  // 다시 조회하지 않고 화면에 반영한다.
  const beginEditExperimentNote = useCallback((text: string) => {
    setExperimentNote({ text, saving: false, error: null });
  }, []);
  const changeExperimentNote = useCallback((text: string) => {
    setExperimentNote((prev) => (prev === null ? prev : { ...prev, text }));
  }, []);
  const cancelEditExperimentNote = useCallback(() => {
    setExperimentNote((prev) => (prev?.saving ? prev : null));
  }, []);
  // 서버에 있는(목록 캐시의) 노트. 목록이 아직 없으면 null 이고, "노트 없음"과 같게 본다.
  const savedExperimentNote = useCallback((experimentId: string): string | null => {
    const state = experimentsStateRef.current;
    return state.status === "success"
      ? (state.data.find((item) => item.id === experimentId)?.description ?? null)
      : null;
  }, []);
  // 초안을 상류에 쓰고 목록 캐시를 갱신한 뒤 초안을 닫는다. 실패하면 예외를 던진다. 저장 버튼은 초안의
  // error 에, 제출 직전 자동 저장은 submitError 에 각각 담는다.
  const persistExperimentNote = useCallback(
    async (experimentId: string, draft: ExperimentNoteDraft) => {
      const updated = await updateExperiment(experimentId, {
        description: normalizeExperimentNote(draft.text),
      });
      setExperimentsState((prev) =>
        prev.status === "success"
          ? {
              status: "success",
              data: prev.data.map((item) =>
                item.id === experimentId ? { ...item, description: updated.description } : item,
              ),
            }
          : prev,
      );
      setExperimentNote(null);
    },
    [],
  );
  const saveExperimentNote = useCallback(async () => {
    const draft = experimentNoteRef.current;
    const experimentId = formRef.current.experimentId;
    if (draft === null || experimentId === "") {
      return;
    }
    // 서버 값과 같아진 초안은 보낼 값이 없다. 요청 없이 닫는다.
    if (!experimentNoteChanged(draft.text, savedExperimentNote(experimentId))) {
      setExperimentNote(null);
      return;
    }
    setExperimentNote({ ...draft, saving: true, error: null });
    try {
      await persistExperimentNote(experimentId, draft);
    } catch (error) {
      setExperimentNote({
        ...draft,
        saving: false,
        error: resolveSubmitErrorMessage(error, features.ownership),
      });
    }
  }, [features.ownership, persistExperimentNote, savedExperimentNote]);

  // 새 실험 생성 다이얼로그 열기/닫기와 제출. 생성 후 실험 목록을 다시 받아 새 실험을 선택한다.
  const openCreateExperiment = useCallback(() => {
    setExperimentDialog({ submitting: false, error: null });
  }, []);
  const closeCreateExperiment = useCallback(() => {
    setExperimentDialog((prev) => (prev?.submitting ? prev : null));
  }, []);
  const submitCreateExperiment = useCallback(
    async (values: { name: string; description: string }) => {
      const workspaceId = formRef.current.workspaceId;
      if (workspaceId === "") {
        return;
      }
      setExperimentDialog({ submitting: true, error: null });
      try {
        const description = values.description.trim();
        const created = await createExperiment({
          training_workspace_id: workspaceId,
          name: values.name.trim(),
          description: description === "" ? undefined : description,
        });
        // 생성 응답을 목록에 반영해 공용 experiment 목록을 다시 조회하지 않는다.
        const items =
          features.ownership === "shared"
            ? upsertExperiment(
                experimentsStateRef.current.status === "success"
                  ? experimentsStateRef.current.data
                  : [],
                {
                  id: created.training_experiment_id,
                  workspace_id: workspaceId,
                  name: created.name,
                  description: created.description,
                },
              )
            : (await listMyExperiments()).filter((item) => item.workspace_id === workspaceId);
        setExperimentsState({ status: "success", data: items });
        setForm((prev) =>
          prev.workspaceId === workspaceId
            ? {
                ...prev,
                experimentId: created.training_experiment_id,
                experimentName: created.name,
              }
            : prev,
        );
        setExperimentDialog(null);
      } catch (error) {
        setExperimentDialog({
          submitting: false,
          error: resolveSubmitErrorMessage(error, features.ownership),
        });
      }
    },
    [features.ownership],
  );

  // session 모드는 내 스코프(me)만 받는다(submit-options 의 팀 전체는 쓰지 않는다). schedule 모드는 그룹 공용
  // workspace 목록을 쓴다. Schedule 은 거기서 만들어지고, 등록된 workspace 가 없으면 만들 곳이 없다. 있으면 첫
  // 번째를 고르고, 그 결과 실험까지 async 로드·자동 선택된다. session 화면에는 이 단계가 없으므로
  // 실패 문구가 유일한 진단이다.
  useEffect(() => {
    let cancelled = false;
    setWorkspacesState({ status: "loading" });
    const load: Promise<TrainingWorkspace[]> =
      features.ownership === "shared"
        ? listSharedWorkspaces().then((rows) =>
            rows.map((row) => ({ id: row.training_workspace_id, name: row.workspace_name })),
          )
        : listMyWorkspaces();
    load
      .then((items) => {
        if (cancelled) {
          return;
        }
        if (features.ownership === "shared" && items.length === 0) {
          setWorkspacesState({ status: "error", message: NO_SHARED_WORKSPACE_MESSAGE });
          return;
        }
        setWorkspacesState({ status: "success", data: items });
        if (items.length > 0 && formRef.current.workspaceId === "") {
          applyWorkspaceScope(items[0].id);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setWorkspacesState({
            status: "error",
            message: isNoTrainingTeamError(error)
              ? NO_TEAM_MESSAGE
              : resolveLoadErrorMessage(error),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [applyWorkspaceScope, features.ownership]);

  const updateConfig = useCallback((patch: Partial<SubmitConfigState>) => {
    setForm((prev) => ({ ...prev, config: { ...prev.config, ...patch } }));
  }, []);

  // 본문 직접 편집으로 바꿀 수 없는 이유(세그먼트 비활성 사유이자 enterDirectEdit 의 게이트).
  // 원본을 읽지 못하면 막는다. applyOverridesToYaml 이 파싱할 수 없는 본문을 그대로 반환해서 override
  // 행이 알림 없이 사라진다.
  const directEditBlockedReason = isDirectConfigEdit(form.config)
    ? null
    : configBodyLoading
      ? "본문을 불러오고 있습니다."
      : form.config.inlineYaml === ""
        ? "config 본문을 불러온 뒤에 직접 편집할 수 있습니다."
        : parsedConfig !== null && parsedConfig.errors.length > 0
          ? "원본 YAML 을 읽지 못해 override 를 본문에 합칠 수 없습니다. overrides 방식으로 진행해 주세요."
          : null;

  // overrides → 본문 직접 편집. 현재 사용자 override 행을 본문에 반영해서 에디터를 열고 목록을 비운다.
  // 두 방식이 배타라 두 입력 위치가 같은 값을 덮어쓰지 않는다. 잃는 값이 없으므로 확인 없이 바로 바꾼다.
  // auto-LR 은 override 목록에 행을 쓰는 유일한 노브라 함께 끈다(값은 행과 함께 본문에 반영되었다).
  // 돌아올 때 복원할 행·토글은 스냅샷에 둔다. updater 가 두 번 호출되어도(StrictMode) 같은 값을 쓴다.
  const enterDirectEdit = useCallback(() => {
    if (directEditBlockedReason !== null) {
      return;
    }
    setConfigEditDiscarded(false);
    setForm((prev) => {
      if (isDirectConfigEdit(prev.config)) {
        return prev;
      }
      overridesBeforeDirectEditRef.current = {
        overrides: prev.config.overrides,
        autoLr: prev.autoLr,
        sigmaWrists: prev.sigmaDefaults?.wrists,
        sigmaRemovedWrists: prev.sigmaDefaults?.removedWrists,
        sigmaImageSize: prev.sigmaDefaults?.imageSize,
        imageSize: prev.config.imageSize,
      };
      return {
        ...prev,
        autoLr: false,
        config: {
          ...prev.config,
          editedYaml: bakeOverridesIntoYaml(prev.config),
          overrides: [],
        },
      };
    });
  }, [directEditBlockedReason]);

  // 본문 직접 편집 → overrides. 편집본을 버리고 전환 전 행·토글을 복원한다. 제출은 다시 repo_config 로
  // 보낸다. 편집이 있을 때의 확인은 화면이 맡는다(커밋하지 않은 초안을 아는 곳이 화면뿐이다).
  const leaveDirectEdit = useCallback(() => {
    setConfigEditDiscarded(false);
    const snapshot = overridesBeforeDirectEditRef.current;
    overridesBeforeDirectEditRef.current = null;
    setForm((prev) => {
      const restored = restoreOverridesBeforeDirectEdit(prev, snapshot);
      return { ...restored, config: { ...restored.config, editedYaml: null } };
    });
  }, []);

  // 직접 편집 중 본문만 레포 원본으로 되돌린다. 방식은 그대로다(방식을 되돌리려면 leaveDirectEdit).
  const resetDirectEditToOriginal = useCallback(() => {
    setForm((prev) =>
      isDirectConfigEdit(prev.config)
        ? { ...prev, config: { ...prev.config, editedYaml: prev.config.inlineYaml } }
        : prev,
    );
  }, []);

  // 본문 편집. 직접 편집 방식일 때만 받는다. overrides 방식에서 본문이 써지면 두 입력 위치가 겹친다.
  // 다시 타이핑하면 "편집이 버려졌다" 안내는 역할을 다했으므로 제거한다.
  const editConfigYaml = useCallback((editedYaml: string) => {
    setConfigEditDiscarded(false);
    setForm((prev) =>
      isDirectConfigEdit(prev.config) ? { ...prev, config: { ...prev.config, editedYaml } } : prev,
    );
  }, []);

  // 비전 인코더 입력 해상도. null 로 되돌리면 override 를 보내지 않아 config 값이 그대로 쓰인다.
  const setImageSize = useCallback((imageSize: SubmitConfigState["imageSize"]) => {
    setForm((prev) => ({
      ...prev,
      config: { ...prev.config, imageSize },
      sigmaDefaults: { ...(prev.sigmaDefaults ?? emptySigmaDefaultsState()), imageSize: "manual" },
    }));
  }, []);

  // 체크포인트를 마지막 하나만 남길지. 보낼 값은 예산에서 계산하므로 여기서 고정하지 않는다.
  const setSaveOnlyLastCheckpoint = useCallback((saveOnlyLastCheckpoint: boolean) => {
    setForm((prev) => ({ ...prev, config: { ...prev.config, saveOnlyLastCheckpoint } }));
  }, []);

  // Random background 사용 토글. 끄면 제출 payload/diff 에 자동 delete override 가 추가된다.
  const setRandomBackground = useCallback((enabled: boolean) => {
    setForm((prev) => ({
      ...prev,
      config: { ...prev.config, randomBackgroundEnabled: enabled },
    }));
  }, []);

  // 이 코드가 마지막으로 입력한 auto-LR 값. 끌 때 사용자가 직접 바꾼 lr 은 지우지 않는다.
  // 자동 계산이 넣은 값일 때만 제거해서 사용자가 직접 입력한 값을 보존한다.
  const autoLrWrittenRef = useRef<string | null>(null);

  // LR 자동 계산 동기화. 토글·GPU 수·batch_size override·config 가 바뀌면 lr override 행을
  // 추가/갱신하고, 토글을 끄면 자동으로 추가한 행만 제거한다. 자체 setForm 재실행은 no-op 가드로 멈춘다.
  const directEdit = isDirectConfigEdit(form.config);
  useEffect(() => {
    if (!form.autoLr) {
      const written = autoLrWrittenRef.current;
      if (written === null) {
        return;
      }
      autoLrWrittenRef.current = null;
      setForm((prev) => {
        const rows = prev.config.overrides;
        const row = rows.find((pair) => parseOverrideKey(pair.key).path === LR_OVERRIDE_KEY);
        if (!row || row.value !== written) {
          return prev;
        }
        return {
          ...prev,
          config: { ...prev.config, overrides: rows.filter((pair) => pair !== row) },
        };
      });
      return;
    }
    // 직접 편집 중에는 목록이 비어 있어야 한다. enterDirectEdit 가 토글을 끄지만, 어느 경로로든
    // 켜진 상태로 들어와도 행을 쓰지 않는다.
    if (directEdit) {
      return;
    }
    const data = parsedConfig?.data;
    if (data === undefined) {
      return;
    }
    const rows = form.config.overrides;
    const batchRow = rows.find(
      (pair) => parseOverrideKey(pair.key).path === BATCH_SIZE_OVERRIDE_KEY,
    );
    const result = computeAutoLr(data, form.nodes * form.gpusPerNode, batchRow?.value ?? null);
    if (result === null || result.lr === "") {
      return;
    }
    const index = rows.findIndex((pair) => parseOverrideKey(pair.key).path === LR_OVERRIDE_KEY);
    const written = autoLrWrittenRef.current;
    if (index >= 0 && rows[index].value === result.lr) {
      autoLrWrittenRef.current = result.lr;
      return;
    }
    // 이 코드가 쓴 값과 다르게 사용자가 직접 고친 행은 autoLr 가 덮어쓰지 않는다(사용자 소유로 바뀐다).
    if (index >= 0 && written !== null && rows[index].value !== written) {
      return;
    }
    autoLrWrittenRef.current = result.lr;
    setForm((prev) => {
      const prevRows = prev.config.overrides;
      const prevIndex = prevRows.findIndex(
        (pair) => parseOverrideKey(pair.key).path === LR_OVERRIDE_KEY,
      );
      const nextRows =
        prevIndex >= 0
          ? prevRows.map((pair, i) => (i === prevIndex ? { ...pair, value: result.lr } : pair))
          : [...prevRows, { key: LR_OVERRIDE_KEY, value: result.lr }];
      return { ...prev, config: { ...prev.config, overrides: nextRows } };
    });
  }, [form.autoLr, form.nodes, form.gpusPerNode, form.config.overrides, parsedConfig, directEdit]);

  // 성공 여부를 반환한다. 창 셸이 진행 toast 를 제거하고 결과를 알린다. `submitError` 는 상태라
  // await 직후의 클로저에서는 이전 값이다.
  const submit = useCallback(async (): Promise<SubmitOutcome> => {
    const current = formRef.current;
    const maskBlocked = maskGenerationBlockReason(serverInfoController.getSnapshot());
    if (
      maskBlocked !== null &&
      current.datasets.some((dataset) => dataset.annotationSetUuid !== null)
    ) {
      setSubmitError(maskBlocked);
      return { ok: false };
    }
    // schedule 모드는 초안도 다시 검사한다. Schedule 스텝을 지난 뒤 GPU 총 수가 바뀌면 그때 통과한
    // 조건이 지금은 범위 밖일 수 있다(확정 스텝은 되돌아가지 않으므로 여기가 마지막 게이트다).
    if (
      !canSubmitForm(current) ||
      sigmaDefaultsLoading ||
      !isaacEvaluationPlan.ready ||
      (hasScheduleStep(features) && !isScheduleDraftReady(scheduleDraftRef.current, totalGpus))
    ) {
      if (isaacEvaluationPlan.errorMessage) {
        setSubmitError(isaacEvaluationPlan.errorMessage);
      }
      return { ok: false };
    }
    setSubmitting(true);
    setSubmitError(null);
    setEvalWarnings([]);
    try {
      // 저장하지 않은 실험 노트는 세션보다 먼저 쓴다. 제출이 끝나면 위저드를 떠나 초안이 사라진다.
      // 노트 저장이 실패하면 세션도 보내지 않는다. 사용자가 적은 내용을 알림 없이 잃는 것보다 한 번 더
      // 누르게 하는 비용이 작다. 어느 요청이 실패했는지는 문구 앞부분이 표시한다.
      const noteDraft = experimentNoteRef.current;
      if (
        noteDraft !== null &&
        experimentNoteChanged(noteDraft.text, savedExperimentNote(current.experimentId))
      ) {
        try {
          await persistExperimentNote(current.experimentId, noteDraft);
        } catch (error) {
          setSubmitError(
            `${EXPERIMENT_NOTE_SAVE_FAILED_PREFIX}${resolveSubmitErrorMessage(error, features.ownership)}`,
          );
          return { ok: false };
        }
      }
      let isaacEvaluation: IsaacEvaluationReq | undefined;
      if (isaacEvaluationEnabled) {
        const benchmarkImage = isaacEvaluationParams.imageUri;
        if (benchmarkImage === null) {
          throw new Error("Check the Isaac Evaluation settings.");
        }
        const { scenarios, warnings } = await reconcileSeedScenarios({
          benchmarkImage,
          values: isaacEvaluationParams.values,
        });
        setEvalWarnings(warnings);
        isaacEvaluation = {
          scenarios: scenarios.map((scenario) => ({
            scenario_id: scenario.scenarioId,
            name: scenario.name,
            seed: scenario.seed ?? undefined,
          })),
        };
      }
      const payload = buildSubmitPayload(current, isaacEvaluation);
      if (hasScheduleStep(features)) {
        // 같은 제출 payload 에 반복 축·실행 게이트만 추가한다. 스케줄이 단건 제출의 확장이라는
        // 계약(BE submit 필드 동일 타입)을 여기서 코드로 구현한다. 단건 POST 라 멱등 키가 없다.
        // 사용 가능하지 않은 게이트는 false 로 바꾼다. 스텝이 토글을 잠가도 초안 값은 남아 있어서(켠 뒤
        // 구성을 바꾼 경우), 그대로 보내면 상류가 422 를 준다. 화면이 잠근 값은 wire 에도 없어야 한다.
        const draft = scheduleDraftRef.current;
        const scheduleView = await createTrainingSchedule(
          {
            ...scheduleCreateFields(
              {
                ...draft,
                skipIfUnchanged: draft.skipIfUnchanged && canFollowBranchTip(current),
                trackLatestEvalRelease: draft.trackLatestEvalRelease && isaacEvaluationEnabled,
              },
              totalGpus,
            ),
            submit: payload,
          },
          role,
        );
        onScheduled?.(scheduleView);
        return { ok: true };
      }
      const view = await submitTrainingSession(payload, idempotencyKeyRef.current);
      onSubmitted(view);
      return { ok: true };
    } catch (error) {
      if (isMaskGenerationDisabledError(error)) serverInfoController.invalidateCapabilities();
      setSubmitError(resolveSubmitErrorMessage(error, features.ownership));
      return { ok: false };
    } finally {
      setSubmitting(false);
    }
  }, [
    isaacEvaluationEnabled,
    isaacEvaluationParams,
    isaacEvaluationPlan,
    features,
    onScheduled,
    onSubmitted,
    role,
    persistExperimentNote,
    savedExperimentNote,
    totalGpus,
    sigmaDefaultsLoading,
  ]);

  const stepIndex = steps.indexOf(step);
  // 한 스텝의 '다음'을 여는 조건. 구성 스텝은 폼 게이트에 컨트롤러만 아는 두 조건을 추가한다.
  //   - 편집본을 YAML 로 읽을 수 있는지(파싱 결과는 폼이 아니라 여기 있다).
  //   - Isaac Evaluation 이 준비되었는지(이미지 메타가 비동기다). 평가를 껐거나 섹션이 없으면 plan 은 항상 ready 다.
  // 스케줄은 eval 추종·Isaac Evaluation 이 전제라 simulation Dataset 이 있어야 한다. 이 한정을 위저드
  // 문구와 picker 목록 좁힘으로 **선언만** 하고 제출 경로에서 판정하지 않으면, 출처를 북마크로 바꿀 때
  // (상류가 북마크 목록에서 environment 를 무시한다) 그대로 통과한다. 판정 SoT 는
  // `hasSimulationDataset` 하나다(rules §6). 모르는 동안(로딩·실패)에는 Isaac 섹션과 같은 규칙으로
  // 잠근다.
  const datasetEnvironmentReady = features.datasetEnvironment === null || simulationReady;
  const canLeaveStep = (candidate: SubmitStep): boolean =>
    isStepComplete(candidate, form) &&
    (candidate !== "configure" || (!configEditInvalid && isaacEvaluationPlan.ready)) &&
    (candidate !== "dataset" || datasetEnvironmentReady) &&
    // 스케줄 초안은 폼 밖(컨트롤러 소유)이라 폼 게이트가 판정할 수 없다. 여기서 추가한다.
    (candidate !== "schedule" || isScheduleDraftReady(scheduleDraft, totalGpus));
  // 앞으로 이동할 수 있는 한계선. 첫 미완성 스텝까지만 갈 수 있다. 뒤로 가는 것은 잠그지 않는다
  // (고친 값을 다시 확인하는 경로). 화살표·탭·푸터 '다음'이 이 한 값을 공유해야 어느 경로로도
  // 게이트를 우회할 수 없다.
  const firstBlockedIndex = steps.findIndex((candidate) => !canLeaveStep(candidate));
  const maxReachableStepIndex = firstBlockedIndex === -1 ? steps.length - 1 : firstBlockedIndex;
  const goNext = useCallback(() => {
    setStep((prev) => {
      const index = steps.indexOf(prev);
      return index < steps.length - 1 ? steps[index + 1] : prev;
    });
  }, [steps]);
  const goBack = useCallback(() => {
    setStep((prev) => {
      const index = steps.indexOf(prev);
      return index > 0 ? steps[index - 1] : prev;
    });
  }, [steps]);
  // 구성 스텝 내비의 항목. 스텝(ConfigureStep)과 ↑/↓ 이동이 같은 목록을 읽어야 하므로 여기서
  // 한 번 만든다. Isaac Evaluation 은 권한이 없으면 목록에서 제거되고(null), 권한이 있는데
  // 시뮬레이션 데이터셋이 없으면 사유를 가진 잠긴 항목(모듈 상수)으로 남는다.
  const evaluationSection = useMemo<EvaluationSectionInput | LockedEvaluationSection | null>(
    () =>
      !canUseIsaacEvaluation
        ? null
        : simulationReady
          ? {
              enabled: isaacEvaluationEnabled,
              ready: isaacEvaluationPlan.ready,
              imageTag: isaacEvaluationParams.selectedTag,
              task: isaacEvaluationPlan.task,
            }
          : SIMULATION_LOCKED_EVALUATION,
    [
      canUseIsaacEvaluation,
      simulationReady,
      isaacEvaluationEnabled,
      isaacEvaluationPlan.ready,
      isaacEvaluationPlan.task,
      isaacEvaluationParams.selectedTag,
    ],
  );
  const configureSections = useMemo(
    () => buildConfigureSections(form, evaluationSection),
    [form, evaluationSection],
  );
  const selectableSections = selectableConfigureSections(isaacEvaluationAvailable);
  const configureSection = resolveSelectableConfigureSection(
    configureSectionRaw,
    selectableSections,
  );
  // 창 셸의 ↑/↓ 가 호출한다. 잠긴 섹션이 아니라 되돌린 섹션에서 출발하고, 잠긴 항목은 건너뛰며,
  // 끝에서는 멈춘다(adjacentConfigureSection 이 null 을 반환한다).
  const moveConfigureSection = useCallback(
    (direction: 1 | -1) => {
      setConfigureSection((prev) => {
        const from = resolveSelectableConfigureSection(prev, selectableSections);
        return adjacentConfigureSection(from, direction, selectableSections) ?? from;
      });
    },
    [selectableSections],
  );

  // 레시피 출처(브랜치·커밋)가 바뀌어 config 를 다시 받는 중인지. 목록 → 본문 조회의 어느 단계든 true 다.
  // 옆 열 diff·config 필드가 이 값으로 스피너를 표시한다. 커밋 목록은 diff 와 관계없어서 포함하지 않는다.
  const recipeReloading = branchConfigsState.status === "loading" || configBodyLoading;

  // override 행이 없으면 본문을 파싱해 읽으므로 폼이 바뀔 때만 계산한다.
  const saveEveryForLastOnly = useMemo(
    () => saveOnlyLastCheckpointSaveEvery(form.config),
    [form.config],
  );

  return {
    optionsState,
    workspacesState,
    // schedule 모드의 공용 Workspace 드롭다운. 고르면 실험 선택을 비우고 그 workspace 로 다시 선택한다.
    selectWorkspace: applyWorkspaceScope,
    form,
    // 이 위저드가 표시하는 항목과 고를 수 있는 항목. 창 셸과 스텝이 모두 이 표를 읽는다.
    features,
    scheduleDraft,
    updateScheduleDraft,
    step,
    stepIndex,
    experimentsState,
    experimentDialog,
    experimentNote,
    branchConfigsState,
    // 고른 브랜치의 최근 커밋(fine-tune 이면 Policy 학습 시점 이후). 실패해도 제출은 막지 않는다.
    commitsState,
    // base Policy 가 학습된 커밋으로 맞추라는 제안(없거나 이미 그 커밋이면 null).
    finetuneRevisionSuggestion,
    // "Branch 최신"이 가리키는 커밋(모르면 null). 센티널 라벨과 확정 요약이 쓴다.
    branchTipSha,
    configBodyLoading,
    recipeReloading,
    // 선택된 config 의 계열(override 키 후보 필터용). 파싱 전이거나 알 수 없으면 null.
    configFamily: activeParsedConfig?.family ?? null,
    // 파싱된 config 트리. override 후보 평탄화·현재 값 미리보기·RandomBackground/mux 감지에 쓴다.
    configData: activeParsedConfig?.data ?? null,
    // 편집본 YAML 파싱 오류. 편집기 아래에 그대로 표시한다. 오류를 무시하지 않는다.
    configParseErrors,
    configEditDiscarded,
    structureState,
    sigmaDefaultsNotice,
    // 입력별 어노테이션 세트 후보와 그 적재 상태. 담은 카드의 드롭다운 소스다.
    annotationSets,
    maskGenerationBlocked,
    // 프리필로 승계한 세트를 쓸 수 없어서 비운 입력 수(0 이면 안내가 없다).
    droppedAnnotationSetCount,
    // 세트 선택과 mask co-train 스위치가 어긋났을 때의 안내(맞으면 null).
    annotationMaskNotice,
    confirmPreviewState,
    confirmValidationState,
    initPolicyScope,
    initPolicyState,
    reservedInitOverrides,
    // 브랜치에 finetune 기본 레시피가 없을 때만 문구가 있다.
    finetuneConfigNotice,
    // 재학습 진입점이 정한 소스 세션(없으면 retrain 축을 고를 수 없다).
    retrainSource,
    // 재학습이 원본 config 를 그대로 쓸 때만 문구가 있다.
    retrainConfigNotice,
    setInitStrategy,
    setInitPolicy,
    setInitPolicyScope,
    toggleChannel,
    // 서버 원본 본문. 확정 스텝의 diff 기준이다(받지 못했으면 빈 문자열).
    sourceYaml,
    submitting,
    submitError,
    evalWarnings,
    // 시뮬레이션 데이터셋이 있어서 Isaac Evaluation 섹션을 고를 수 있는지(없으면 내비에 잠긴 항목으로
    // 남는다). 아래 enabled 는 이 값으로 마스킹되어 있다.
    isaacEvaluationAvailable,
    isaacEvaluationEnabled,
    isaacEvaluationParams,
    isaacEvaluationPlan,
    setIsaacEvaluationEnabled,
    canAdvance: canLeaveStep(step),
    maxReachableStepIndex,
    canSubmit:
      (maskGenerationBlocked === null ||
        form.datasets.every((dataset) => dataset.annotationSetUuid === null)) &&
      canSubmitForm(form) &&
      !sigmaDefaultsLoading &&
      isaacEvaluationPlan.ready &&
      confirmValidationState.status !== "loading" &&
      datasetEnvironmentReady &&
      (!hasScheduleStep(features) || isScheduleDraftReady(scheduleDraft, totalGpus)),
    updateForm,
    setDatasets,
    selectExperiment,
    openCreateExperiment,
    closeCreateExperiment,
    submitCreateExperiment,
    beginEditExperimentNote,
    changeExperimentNote,
    cancelEditExperimentNote,
    saveExperimentNote,
    selectBranch,
    selectCommit,
    applyLearningCoreRevision,
    selectConfig,
    updateConfig,
    editConfigYaml,
    enterDirectEdit,
    leaveDirectEdit,
    resetDirectEditToOriginal,
    // 본문 직접 편집 세그먼트를 열 수 없는 이유(열 수 있으면 null). 세그먼트의 disabledTooltip 이 된다.
    directEditBlockedReason,
    setImageSize,
    // config 가 가진 현재 해상도. 폼이 값을 바꾸지 않았을 때 해상도 필드의 자동 입력 값이 된다.
    configImageSize: activeParsedConfig === null ? null : imageSizeAt(activeParsedConfig.data),
    setSaveOnlyLastCheckpoint,
    // 예산에서 계산한 저장 주기. 끈 상태이거나 학습 길이를 읽을 수 없으면 null 이라 노브가 닫힌다.
    saveOnlyLastCheckpointSaveEvery: saveEveryForLastOnly,
    // `train.budget` 이 없는 레시피에는 저장 주기 노브가 없다.
    budgetForm: activeParsedConfig !== null && isBudgetForm(activeParsedConfig.data),
    setRandomBackground,
    setStep,
    goNext,
    goBack,
    configureSections,
    configureSection,
    setConfigureSection,
    moveConfigureSection,
    submit,
  };
}

export type SubmitWizardController = ReturnType<typeof useSubmitWizardController>;
